import { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { uploadFileDirect } from "@/lib/directUpload";
import { getBenchmarks, DMS_OPTIONS, MARKETING_PLATFORM_OPTIONS, BENCHMARK_RANGES } from "@/lib/onboardingBenchmarks";
import type { DealerInventoryType } from "@/lib/onboardingBenchmarks";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FormattedNumberInput } from "@/components/formatted-number-input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip as UITooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  CheckCircle2, ChevronRight, ChevronLeft, Info, Upload, Zap, BarChart3,
  Building2, Users, ShoppingCart, DollarSign, Clock, Truck, PiggyBank, FileText,
  Megaphone, Shield, AlertTriangle, RefreshCw, Star, X, Plus, GripVertical,
  ArrowRight, CheckCheck, Sparkles, Lock, Pencil,
} from "lucide-react";

// ─── US States list ───────────────────────────────────────────────────────────

const US_STATES = [
  { code: "AL", name: "Alabama" }, { code: "AK", name: "Alaska" }, { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" }, { code: "CA", name: "California" }, { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" }, { code: "DE", name: "Delaware" }, { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" }, { code: "HI", name: "Hawaii" }, { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" }, { code: "IN", name: "Indiana" }, { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" }, { code: "KY", name: "Kentucky" }, { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" }, { code: "MD", name: "Maryland" }, { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" }, { code: "MN", name: "Minnesota" }, { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" }, { code: "MT", name: "Montana" }, { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" }, { code: "NH", name: "New Hampshire" }, { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" }, { code: "NY", name: "New York" }, { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" }, { code: "OH", name: "Ohio" }, { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" }, { code: "PA", name: "Pennsylvania" }, { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" }, { code: "SD", name: "South Dakota" }, { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" }, { code: "UT", name: "Utah" }, { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" }, { code: "WA", name: "Washington" }, { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" }, { code: "WY", name: "Wyoming" },
];

// ─── Types ────────────────────────────────────────────────────────────────────

interface VendorRow {
  id: string;
  name: string;
  category: string;
  monthlyCost: number | "";
  leadsPerMonth?: number | "";
  salesPerMonth?: number | "";
}

interface EmployeeRow {
  role: string;
  count: number;
}

interface PhaseData {
  // Phase 1
  dealershipName?: string;
  address?: string;
  city?: string;
  zipCode?: string;
  stateCode?: string;
  avgSalesPerMonth?: number | "";
  avgGrossProfitPerUnit?: number | "";
  avgVehicleValue?: number | "";
  avgFixedCosts?: number | "";
  targetTurnDays?: number | "";
  avgDaysPurchaseToFrontline?: number | "";
  avgMonthlyRevenue?: number | "";
  inventoryType?: string;
  numRooftops?: number | "";
  avgUnitsOnLot?: number | "";
  lotCapacity?: number | "";
  totalHeadcount?: number | "";
  // Phase 2
  dmsSystem?: string;
  dmsMonthlyCost?: number | "";
  vendors?: VendorRow[];
  employees?: EmployeeRow[];
  // Phase 3
  lendingPrimePct?: number;
  lendingSubprimePct?: number;
  lendingBhphPct?: number;
  lendingCashPct?: number;
  primaryLenderNames?: string;
  primaryLenders?: Array<{ name: string; type: "prime" | "subprime" | "mix"; isPrimary: boolean }>;
  // Phase 4
  avgFrontEndGross?: number | "";
  avgBackEndGross?: number | "";
  avgVariableCostPerUnit?: number | "";
  avgReconCost?: number | "";
  avgTransportFee?: number | "";
  // Phase 5
  floorplanLenders?: string[];
  avgFloorplanRate?: number | "";
  // Phase 6
  sourcingAuctionPct?: number;
  sourcingTradeInPct?: number;
  sourcingPrivatePartyPct?: number;
  sourcingDealerToDealerPct?: number;
  primaryAuctionLanes?: string[];
  // Phase 7
  cashReservesRange?: string;
  hasLineOfCredit?: boolean;
  lineOfCreditRange?: string;
  hasNonFloorplanDebt?: boolean;
  nonFloorplanDebtMonthly?: number | "";
  bhphReceivablesRange?: string;
  // Phase 3 (marketing aggregates, computed from vendor data)
  monthlyLeadVolume?: number | "";
  leadToSaleConversionPct?: number;
  leadSourcesRanked?: string[];
  tracksRepeatReferral?: boolean;
  repeatReferralPct?: number;
}

interface AiFlag {
  field: string;
  phase: number;
  dealerValue: any;
  benchmarkRange: string;
  explanation: string;
  action?: "keep" | "update";
}

interface ConfidenceBreakdown {
  baseScore: number;
  statementBonus: number;
  validationBonus: number;
  filledCount: number;
  totalFields: number;
}

// ─── Confidence key fields (mirrors server/routes.ts KEY_FIELDS) ──────────────
const CONFIDENCE_KEY_FIELDS: Array<{ key: string; label: string }> = [
  { key: "dealershipName", label: "Dealership Name" },
  { key: "inventoryType", label: "Inventory Type" },
  { key: "avgUnitsOnLot", label: "Avg Units on Lot" },
  { key: "lotCapacity", label: "Lot Capacity" },
  { key: "totalHeadcount", label: "Total Headcount" },
  { key: "dmsSystem", label: "DMS System" },
  { key: "avgSalesPerMonth", label: "Avg Sales Per Month" },
  { key: "lendingPrimePct", label: "Prime Lending %" },
  { key: "lendingSubprimePct", label: "Subprime Lending %" },
  { key: "avgFrontEndGross", label: "Avg Front-End Gross Profit" },
  { key: "avgBackEndGross", label: "Avg Back-End Gross" },
  { key: "avgFixedCosts", label: "Avg Fixed Costs" },
  { key: "avgReconCost", label: "Avg Recon Cost" },
  { key: "avgFloorplanRate", label: "Avg Floorplan Rate" },
  { key: "targetTurnDays", label: "Target Turn Days" },
  { key: "sourcingAuctionPct", label: "Sourcing — Auction %" },
  { key: "sourcingTradeInPct", label: "Sourcing — Trade-In %" },
  { key: "primaryAuctionLanes", label: "Primary Auction Lanes" },
  { key: "monthlyLeadVolume", label: "Monthly Lead Volume" },
  { key: "leadToSaleConversionPct", label: "Lead-to-Sale Conversion %" },
];

// ─── Metadata for inline missing-field inputs ────────────────────────────────

type InlineFieldType = "number" | "text" | "select" | "auction_lanes" | "tags";

interface InlineFieldMeta {
  type: InlineFieldType;
  group: string;
  prefix?: string;
  suffix?: string;
  placeholder?: string;
  options?: Array<{ value: string; label: string }>;
  min?: number;
  max?: number;
}

const MISSING_FIELD_META: Record<string, InlineFieldMeta> = {
  dealershipName: { type: "text", group: "Profile", placeholder: "e.g. Smith Motors" },
  inventoryType: {
    type: "select", group: "Profile",
    options: [
      { value: "used_retail", label: "Retail (Used)" },
      { value: "retail_wholesale", label: "Retail / Wholesale" },
      { value: "wholesale", label: "Wholesale" },
      { value: "bhph", label: "Buy Here Pay Here" },
      { value: "franchise", label: "Franchise" },
    ],
  },
  avgUnitsOnLot: { type: "number", group: "Profile", suffix: "units", placeholder: "45", min: 0 },
  lotCapacity: { type: "number", group: "Profile", suffix: "units", placeholder: "60", min: 0 },
  totalHeadcount: { type: "number", group: "Profile", suffix: "people", placeholder: "12", min: 0 },
  dmsSystem: {
    type: "select", group: "DMS & Team",
    // Reuse the shared DMS_OPTIONS constant (from onboardingBenchmarks) so the
    // inline form always stays in sync with the Phase 3 tab's picker.
    options: DMS_OPTIONS.map((o) => ({ value: o.label, label: o.label })),
  },
  avgSalesPerMonth: { type: "number", group: "Sales & Lending", suffix: "units/mo", placeholder: "30", min: 0 },
  lendingPrimePct: { type: "number", group: "Sales & Lending", suffix: "%", placeholder: "15", min: 0, max: 100 },
  lendingSubprimePct: { type: "number", group: "Sales & Lending", suffix: "%", placeholder: "40", min: 0, max: 100 },
  avgFrontEndGross: { type: "number", group: "Profitability", prefix: "$", placeholder: "2,800", min: 0 },
  avgBackEndGross: { type: "number", group: "Profitability", prefix: "$", placeholder: "600", min: 0 },
  avgFixedCosts: { type: "number", group: "Profitability", prefix: "$", suffix: "/mo", placeholder: "28,000", min: 0 },
  avgReconCost: { type: "number", group: "Profitability", prefix: "$", placeholder: "1,100", min: 0 },
  avgFloorplanRate: { type: "number", group: "Inventory & Sourcing", suffix: "% APR", placeholder: "18.5", min: 0, max: 100 },
  targetTurnDays: { type: "number", group: "Inventory & Sourcing", suffix: "days", placeholder: "35", min: 1 },
  sourcingAuctionPct: { type: "number", group: "Inventory & Sourcing", suffix: "%", placeholder: "55", min: 0, max: 100 },
  sourcingTradeInPct: { type: "number", group: "Inventory & Sourcing", suffix: "%", placeholder: "25", min: 0, max: 100 },
  primaryAuctionLanes: { type: "auction_lanes", group: "Inventory & Sourcing" },
  monthlyLeadVolume: { type: "number", group: "Marketing", suffix: "leads/mo", placeholder: "180", min: 0 },
  leadToSaleConversionPct: { type: "number", group: "Marketing", suffix: "%", placeholder: "17", min: 0, max: 100 },
};

function computeMissingFields(allFieldValues: Record<string, any>): Array<{ key: string; label: string }> {
  const normalized = { ...allFieldValues };
  return CONFIDENCE_KEY_FIELDS.filter((f) => {
    const v = normalized[f.key];
    if (v == null || v === "") return true;
    if (Array.isArray(v) && v.length === 0) return true;
    return false;
  });
}

// ─── Helper Components ────────────────────────────────────────────────────────

function FieldTooltip({ children, content }: { children: React.ReactNode; content: string }) {
  return (
    <UITooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side="top" className="max-w-[240px] text-xs">{content}</TooltipContent>
    </UITooltip>
  );
}

function SectionContext({ text }: { text: string }) {
  return (
    <div className="flex items-start gap-2.5 mb-6 p-4 rounded-xl bg-primary/5 border border-primary/15">
      <Sparkles className="w-4 h-4 text-primary mt-0.5 shrink-0" />
      <p className="text-sm text-muted-foreground leading-relaxed">{text}</p>
    </div>
  );
}

function AuctionLanesInput({ lanes, onChange }: { lanes: string[]; onChange: (lanes: string[]) => void }) {
  const { t } = useTranslation(["onboarding", "common"]);
  const [draft, setDraft] = useState("");

  const addLane = () => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    onChange([...lanes, trimmed]);
    setDraft("");
  };

  const removeLane = (idx: number) => {
    onChange(lanes.filter((_, i) => i !== idx));
  };

  return (
    <div className="space-y-1.5">
      <Label>{t("onboarding:auctionLanes.label")}</Label>
      <div className="flex gap-2">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addLane(); } }}
          placeholder={t("onboarding:auctionLanes.placeholder")}
          data-testid="input-primaryAuctionLanes"
        />
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={addLane}
          disabled={!draft.trim()}
          data-testid="button-addAuctionLane"
          aria-label={t("onboarding:auctionLanes.add")}
        >
          <Plus className="w-4 h-4" />
        </Button>
      </div>
      {lanes.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pt-1">
          {lanes.map((lane, i) => (
            <span
              key={i}
              className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary border border-primary/20"
              data-testid={`tag-auctionLane-${i}`}
            >
              {lane}
              <button
                type="button"
                onClick={() => removeLane(i)}
                className="ml-0.5 hover:text-destructive transition-colors"
                data-testid={`button-removeAuctionLane-${i}`}
                aria-label={t("onboarding:auctionLanes.remove", { lane })}
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function InsightCard({ title, items }: { title: string; items: Array<{ label: string; value: string }> }) {
  return (
    <div className="mt-6 p-4 rounded-xl bg-chart-1/8 border border-chart-1/20">
      <div className="flex items-center gap-2 mb-3">
        <BarChart3 className="w-4 h-4 text-chart-1" />
        <p className="text-sm font-semibold text-chart-1">{title}</p>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {items.map((item) => (
          <div key={item.label}>
            <p className="text-xs text-muted-foreground">{item.label}</p>
            <p className="text-sm font-bold tabular-nums">{item.value}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// Thin wrapper over the shared FormattedNumberInput: callers here work with
// number | "" values and rely on min/max clamping on blur.
function NumericInput({
  value, onChange, prefix, suffix, placeholder, testId, min, max,
}: {
  value: number | "";
  onChange: (v: number | "") => void;
  prefix?: string;
  suffix?: string;
  placeholder?: string;
  testId?: string;
  min?: number;
  max?: number;
  step?: number;
}) {
  return (
    <FormattedNumberInput
      value={value === "" ? null : value}
      onValueChange={(_raw, num) => onChange(num ?? "")}
      min={min}
      max={max}
      prefix={prefix}
      suffix={suffix}
      placeholder={placeholder ?? "0"}
      testId={testId}
      className="text-sm"
    />
  );
}

function PercentSliders({
  fields, values, onChange,
}: {
  fields: Array<{ key: string; label: string; color: string }>;
  values: Record<string, number>;
  onChange: (key: string, val: number) => void;
}) {
  const { t } = useTranslation(["onboarding", "common"]);
  const total = Object.values(values).reduce((a, b) => a + b, 0);

  return (
    <div className="space-y-4">
      {fields.map((f) => (
        <div key={f.key} className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label className="text-sm">{f.label}</Label>
            <span className="text-sm font-bold tabular-nums">{values[f.key] ?? 0}%</span>
          </div>
          <Slider
            value={[values[f.key] ?? 0]}
            onValueChange={([v]) => onChange(f.key, v)}
            min={0}
            max={100}
            step={5}
            className="[&>span]:bg-primary"
            data-testid={`slider-${f.key}`}
          />
        </div>
      ))}
      <div className={`flex items-center gap-2 text-sm font-medium ${Math.abs(total - 100) < 1 ? "text-emerald-600" : "text-amber-600"}`}>
        {Math.abs(total - 100) < 1 ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
        {Math.abs(total - 100) < 1 ? t("onboarding:percentSliders.totalBalanced", { total }) : t("onboarding:percentSliders.totalMustEqual", { total })}
      </div>
    </div>
  );
}


// ─── Helpers ──────────────────────────────────────────────────────────────────

const LEGACY_INVENTORY_MAP: Record<string, string> = {
  used: "used_retail",
  mixed: "used_retail",
};

function normalizeInventoryType(value: string | undefined | null): string | undefined {
  if (!value) return value ?? undefined;
  return LEGACY_INVENTORY_MAP[value] ?? value;
}

// ─── Phase Components ─────────────────────────────────────────────────────────

function Phase1({ data, onChange }: { data: PhaseData; onChange: (d: Partial<PhaseData>) => void }) {
  const { t } = useTranslation(["onboarding", "common"]);
  const invType = (data.inventoryType || "used_retail") as DealerInventoryType;
  const bench = getBenchmarks(invType);

  const salesPerMonth = typeof data.avgSalesPerMonth === "number" ? data.avgSalesPerMonth : null;
  const vehicleValue = typeof data.avgVehicleValue === "number" ? data.avgVehicleValue : null;
  const unitsOnLot = typeof data.avgUnitsOnLot === "number" ? data.avgUnitsOnLot : null;
  const headcount = typeof data.totalHeadcount === "number" ? data.totalHeadcount : null;
  const monthlyRevenue = salesPerMonth !== null && vehicleValue !== null ? salesPerMonth * vehicleValue : null;

  return (
    <div className="space-y-6">
      <SectionContext text={t("onboarding:phase1.context")} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="space-y-1.5 md:col-span-2">
          <Label>{t("onboarding:phase1.dealershipName")}</Label>
          <Input
            value={data.dealershipName ?? ""}
            onChange={(e) => onChange({ dealershipName: e.target.value })}
            placeholder={t("onboarding:phase1.dealershipNamePlaceholder")}
            data-testid="input-dealershipName"
          />
        </div>

        <div className="space-y-1.5 md:col-span-2">
          <Label>{t("onboarding:phase1.streetAddress")}</Label>
          <Input
            value={data.address ?? ""}
            onChange={(e) => onChange({ address: e.target.value })}
            placeholder={t("onboarding:phase1.streetAddressPlaceholder")}
            data-testid="input-address"
          />
        </div>

        <div className="space-y-1.5">
          <Label>{t("onboarding:phase1.city")}</Label>
          <Input
            value={data.city ?? ""}
            onChange={(e) => onChange({ city: e.target.value })}
            placeholder={t("onboarding:phase1.cityPlaceholder")}
            data-testid="input-city"
          />
        </div>

        <div className="space-y-1.5">
          <Label>{t("onboarding:phase1.state")}</Label>
          <Select
            value={data.stateCode ?? ""}
            onValueChange={(val) => onChange({ stateCode: val })}
          >
            <SelectTrigger data-testid="select-stateCode">
              <SelectValue placeholder={t("onboarding:phase1.selectState")} />
            </SelectTrigger>
            <SelectContent>
              {US_STATES.map((s) => (
                <SelectItem key={s.code} value={s.code}>{s.name} ({s.code})</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label>{t("onboarding:phase1.zipCode")}</Label>
          <Input
            value={data.zipCode ?? ""}
            onChange={(e) => onChange({ zipCode: e.target.value })}
            placeholder={t("onboarding:phase1.zipCodePlaceholder")}
            data-testid="input-zipCode"
            maxLength={10}
          />
        </div>

        <div className="space-y-1.5">
          <FieldTooltip content={t("onboarding:phase1.monthlySalesTip")}>
            <Label className="flex items-center gap-1.5 cursor-help">{t("onboarding:phase1.monthlySales")} <Info className="w-3.5 h-3.5 text-muted-foreground" /></Label>
          </FieldTooltip>
          <NumericInput value={data.avgSalesPerMonth ?? ""} onChange={(v) => onChange({ avgSalesPerMonth: v })} suffix={t("onboarding:units.unitsPerMo")} placeholder={t("onboarding:phase1.eg", { value: bench.avgSalesPerMonth })} testId="input-p1-avgSalesPerMonth" min={1} />
        </div>

        <div className="space-y-1.5">
          <FieldTooltip content={t("onboarding:phase1.grossProfitPerUnitTip")}>
            <Label className="flex items-center gap-1.5 cursor-help">{t("onboarding:phase1.grossProfitPerUnit")} <Info className="w-3.5 h-3.5 text-muted-foreground" /></Label>
          </FieldTooltip>
          <NumericInput value={data.avgGrossProfitPerUnit ?? ""} onChange={(v) => onChange({ avgGrossProfitPerUnit: v })} prefix="$" placeholder={t("onboarding:phase1.eg", { value: (bench.avgFrontEndGross + bench.avgBackEndGross).toLocaleString() })} testId="input-p1-avgGrossProfitPerUnit" min={0} />
        </div>

        <div className="space-y-1.5">
          <FieldTooltip content={t("onboarding:phase1.avgVehicleValueTip")}>
            <Label className="flex items-center gap-1.5 cursor-help">{t("onboarding:phase1.avgVehicleValue")} <Info className="w-3.5 h-3.5 text-muted-foreground" /></Label>
          </FieldTooltip>
          <NumericInput value={data.avgVehicleValue ?? ""} onChange={(v) => onChange({ avgVehicleValue: v })} prefix="$" placeholder={t("onboarding:phase1.avgVehicleValuePlaceholder")} testId="input-p1-avgVehicleValue" min={0} />
        </div>

        <div className="space-y-1.5">
          <FieldTooltip content={t("onboarding:phase1.monthlyOpexTip")}>
            <Label className="flex items-center gap-1.5 cursor-help">{t("onboarding:phase1.monthlyOpex")} <Info className="w-3.5 h-3.5 text-muted-foreground" /></Label>
          </FieldTooltip>
          <NumericInput value={data.avgFixedCosts ?? ""} onChange={(v) => onChange({ avgFixedCosts: v })} prefix="$" suffix={t("onboarding:units.perMo")} placeholder={t("onboarding:phase1.eg", { value: bench.avgFixedCosts.toLocaleString() })} testId="input-p1-avgFixedCosts" min={0} />
        </div>

        <div className="space-y-1.5">
          <FieldTooltip content={t("onboarding:phase1.avgTurnTimeTip")}>
            <Label className="flex items-center gap-1.5 cursor-help">{t("onboarding:phase1.avgTurnTime")} <Info className="w-3.5 h-3.5 text-muted-foreground" /></Label>
          </FieldTooltip>
          <NumericInput value={data.targetTurnDays ?? ""} onChange={(v) => onChange({ targetTurnDays: v })} suffix={t("onboarding:units.days")} placeholder={t("onboarding:phase1.eg", { value: bench.targetTurnDays })} testId="input-p1-targetTurnDays" min={1} max={180} />
        </div>

        <div className="space-y-1.5">
          <FieldTooltip content={t("onboarding:phase1.avgReconDaysTip")}>
            <Label className="flex items-center gap-1.5 cursor-help">{t("onboarding:phase1.avgReconDays")} <Info className="w-3.5 h-3.5 text-muted-foreground" /></Label>
          </FieldTooltip>
          <NumericInput value={data.avgDaysPurchaseToFrontline ?? ""} onChange={(v) => onChange({ avgDaysPurchaseToFrontline: v })} suffix={t("onboarding:units.days")} placeholder={t("onboarding:phase1.eg", { value: bench.avgDaysPurchaseToFrontline })} testId="input-p1-avgDaysPurchaseToFrontline" min={1} max={60} />
        </div>

        <div className="space-y-1.5 md:col-span-2">
          <FieldTooltip content={t("onboarding:phase1.monthlyRevenueTip")}>
            <Label className="flex items-center gap-1.5 cursor-help">{t("onboarding:phase1.monthlyRevenue")} <Info className="w-3.5 h-3.5 text-muted-foreground" /></Label>
          </FieldTooltip>
          <div className="flex items-center h-9 px-3 rounded-md border border-input bg-muted/40 text-sm text-muted-foreground" data-testid="display-p1-avgMonthlyRevenue">
            {monthlyRevenue !== null
              ? <><span className="text-foreground font-medium">${monthlyRevenue.toLocaleString()}<span className="text-muted-foreground font-normal">{t("onboarding:units.perMo")}</span></span><span className="ml-2 text-xs">{t("onboarding:phase1.monthlyRevenueDetail", { sales: salesPerMonth, value: vehicleValue!.toLocaleString() })}</span></>
              : <span>—</span>
            }
          </div>
        </div>

        <div className="space-y-1.5">
          <Label>{t("onboarding:phase1.inventoryType")}</Label>
          <Select value={data.inventoryType ?? ""} onValueChange={(v) => {
            const b = getBenchmarks(v as DealerInventoryType);
            onChange({
              inventoryType: v,
              avgUnitsOnLot: b.avgUnitsOnLot,
              lotCapacity: b.lotCapacity,
              avgSalesPerMonth: b.avgSalesPerMonth,
              totalHeadcount: b.totalHeadcount,
            });
          }}>
            <SelectTrigger data-testid="select-inventoryType">
              <SelectValue placeholder={t("onboarding:phase1.selectType")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="used_retail">{t("onboarding:phase1.invRetail")}</SelectItem>
              <SelectItem value="retail_wholesale">{t("onboarding:phase1.invRetailWholesale")}</SelectItem>
              <SelectItem value="wholesale">{t("onboarding:phase1.invWholesale")}</SelectItem>
              <SelectItem value="bhph">{t("onboarding:phase1.invBhph")}</SelectItem>
              <SelectItem value="franchise">{t("onboarding:phase1.invFranchise")}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <FieldTooltip content={t("onboarding:phase1.numRooftopsTip")}>
            <Label className="flex items-center gap-1.5 cursor-help">{t("onboarding:phase1.numRooftops")} <Info className="w-3.5 h-3.5 text-muted-foreground" /></Label>
          </FieldTooltip>
          <NumericInput value={data.numRooftops ?? 1} onChange={(v) => onChange({ numRooftops: v })} min={1} max={50} testId="input-numRooftops" />
        </div>

        <div className="space-y-1.5">
          <FieldTooltip content={t("onboarding:phase1.avgInventoryOnLotTip", { invType, count: bench.avgUnitsOnLot })}>
            <Label className="flex items-center gap-1.5 cursor-help">{t("onboarding:phase1.avgInventoryOnLot")} <Info className="w-3.5 h-3.5 text-muted-foreground" /></Label>
          </FieldTooltip>
          <NumericInput value={data.avgUnitsOnLot ?? ""} onChange={(v) => onChange({ avgUnitsOnLot: v })} suffix={t("onboarding:units.units")} placeholder={t("onboarding:phase1.eg", { value: bench.avgUnitsOnLot })} testId="input-avgUnitsOnLot" />
        </div>

        <div className="space-y-1.5">
          <Label>{t("onboarding:phase1.lotCapacity")}</Label>
          <NumericInput value={data.lotCapacity ?? ""} onChange={(v) => onChange({ lotCapacity: v })} suffix={t("onboarding:units.units")} placeholder={t("onboarding:phase1.eg", { value: bench.lotCapacity })} testId="input-lotCapacity" />
        </div>

        <div className="space-y-1.5">
          <FieldTooltip content={t("onboarding:phase1.totalHeadcountTip")}>
            <Label className="flex items-center gap-1.5 cursor-help">{t("onboarding:phase1.totalHeadcount")} <Info className="w-3.5 h-3.5 text-muted-foreground" /></Label>
          </FieldTooltip>
          <NumericInput value={data.totalHeadcount ?? ""} onChange={(v) => onChange({ totalHeadcount: v })} suffix={t("onboarding:units.people")} placeholder={t("onboarding:phase1.eg", { value: bench.totalHeadcount })} testId="input-totalHeadcount" />
        </div>
      </div>

    </div>
  );
}

const EMPLOYEE_ROLE_KEYS: Record<string, string> = {
  "Sales": "onboarding:phase2.roleSales",
  "F&I": "onboarding:phase2.roleFi",
  "Service / Recon": "onboarding:phase2.roleServiceRecon",
  "Admin": "onboarding:phase2.roleAdmin",
};

function Phase2({ data, onChange }: { data: PhaseData; onChange: (d: Partial<PhaseData>) => void }) {
  const { t } = useTranslation(["onboarding", "common"]);
  const invType = (data.inventoryType || "used_retail") as DealerInventoryType;
  const bench = getBenchmarks(invType);
  const vendors: VendorRow[] = data.vendors ?? [];
  const employees: EmployeeRow[] = data.employees ?? [
    { role: "Sales", count: bench.salesHeadcount },
    { role: "F&I", count: bench.fiHeadcount },
    { role: "Service / Recon", count: bench.serviceReconHeadcount },
    { role: "Admin", count: bench.adminHeadcount },
  ];

  const dmsMonthlyCost = typeof data.dmsMonthlyCost === "number" ? data.dmsMonthlyCost : (bench.dmsMonthlyCost ?? 0);
  const totalMonthly = vendors.reduce((s, v) => s + (typeof v.monthlyCost === "number" ? v.monthlyCost : 0), 0) + dmsMonthlyCost;
  const salesPerMonth = typeof data.avgSalesPerMonth === "number" ? data.avgSalesPerMonth : bench.avgSalesPerMonth;

  const addVendor = () => {
    onChange({ vendors: [...vendors, { id: crypto.randomUUID(), name: "", category: "marketing", monthlyCost: "" }] });
  };

  const removeVendor = (id: string) => {
    onChange({ vendors: vendors.filter((v) => v.id !== id) });
  };

  const updateVendor = (id: string, key: keyof VendorRow, val: any) => {
    onChange({ vendors: vendors.map((v) => v.id === id ? { ...v, [key]: val } : v) });
  };

  const updateEmployee = (role: string, count: number) => {
    onChange({ employees: employees.map((e) => e.role === role ? { ...e, count } : e) });
  };

  return (
    <div className="space-y-6">
      <SectionContext text={t("onboarding:phase2.context")} />

      {/* DMS Section */}
      {(() => {
        const knownDmsLabels = DMS_OPTIONS.map((o) => o.label);
        const dmsSelectValue = knownDmsLabels.includes(data.dmsSystem ?? "") ? (data.dmsSystem ?? "") : (data.dmsSystem ? "Other" : "");
        const dmsOtherValue = dmsSelectValue === "Other" ? (data.dmsSystem === "__other__" ? "" : (data.dmsSystem ?? "")) : "";
        return (
          <div className="space-y-3">
            <Label className="text-base">{t("onboarding:phase2.dmsTitle")}</Label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="flex items-center gap-1.5 h-5">{t("onboarding:phase2.dmsPlatform")}</Label>
                <Select value={dmsSelectValue} onValueChange={(v) => {
                  if (v === "Other") {
                    onChange({ dmsSystem: "__other__" });
                  } else {
                    onChange({ dmsSystem: v });
                  }
                }}>
                  <SelectTrigger data-testid="select-dmsSystem">
                    <SelectValue placeholder={t("onboarding:phase2.selectDms")} />
                  </SelectTrigger>
                  <SelectContent>
                    {DMS_OPTIONS.map((o) => (
                      <SelectItem key={o.label} value={o.label}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {dmsSelectValue === "Other" && (
                  <Input
                    data-testid="input-dmsSystemOther"
                    placeholder={t("onboarding:phase2.otherDmsPlaceholder")}
                    value={dmsOtherValue}
                    onChange={(e) => onChange({ dmsSystem: e.target.value || "__other__" })}
                    className="mt-2"
                  />
                )}
              </div>
              <div className="space-y-1.5">
                <FieldTooltip content={t("onboarding:phase2.monthlyDmsCostTip")}>
                  <Label className="flex items-center gap-1.5 h-5 cursor-help">{t("onboarding:phase2.monthlyDmsCost")} <Info className="w-3.5 h-3.5 text-muted-foreground" /></Label>
                </FieldTooltip>
                <NumericInput value={data.dmsMonthlyCost ?? ""} onChange={(v) => onChange({ dmsMonthlyCost: v })} prefix="$" suffix={t("onboarding:units.perMo")} testId="input-dmsMonthlyCost" />
              </div>
            </div>
          </div>
        );
      })()}

      <Separator />

      <div className="space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <Label className="text-base">{t("onboarding:phase2.marketingVendorPlatforms")}</Label>
            <p className="text-xs text-muted-foreground mt-0.5">{t("onboarding:phase2.leadSourcesDesc")}</p>
          </div>
          <Button size="sm" variant="outline" onClick={addVendor} data-testid="button-add-vendor" className="gap-1.5 shrink-0">
            <Plus className="w-3.5 h-3.5" /> {t("onboarding:phase2.addRow")}
          </Button>
        </div>

        {vendors.length === 0 && (
          <div className="text-center py-6 text-muted-foreground text-sm border-2 border-dashed rounded-xl">
            {t("onboarding:phase2.noLeadSources")}
          </div>
        )}

        {vendors.map((v) => (
          <div key={v.id} className="border rounded-lg p-3 space-y-2.5" data-testid={`vendor-row-${v.id}`}>
            <div className="flex items-center gap-2">
              <div className="flex-1">
                {(() => {
                  const isKnown = MARKETING_PLATFORM_OPTIONS.some((o) => o.label === v.name);
                  const selectVal = isKnown ? v.name : (v.name ? "Other" : "");
                  return (
                    <Select value={selectVal} onValueChange={(val) => {
                      onChange({
                        vendors: vendors.map((row) =>
                          row.id === v.id ? { ...row, name: val } : row
                        ),
                      });
                    }}>
                      <SelectTrigger className="h-9 text-sm" data-testid={`select-vendor-platform-${v.id}`}>
                        <SelectValue placeholder={t("onboarding:phase2.selectPlatform")} />
                      </SelectTrigger>
                      <SelectContent>
                        {MARKETING_PLATFORM_OPTIONS.map((o) => (
                          <SelectItem key={o.label} value={o.label}>{o.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  );
                })()}
              </div>
              {(v.name === "Other" || (v.name && !MARKETING_PLATFORM_OPTIONS.some((o) => o.label === v.name))) && (
                <Input
                  value={v.name === "Other" ? "" : v.name}
                  onChange={(e) => updateVendor(v.id, "name", e.target.value || "Other")}
                  className="h-9 text-sm flex-1"
                  placeholder={t("onboarding:phase2.vendorName")}
                  data-testid={`input-vendor-name-custom-${v.id}`}
                />
              )}
              <Button size="icon" variant="ghost" onClick={() => removeVendor(v.id)} className="h-9 w-9 shrink-0" data-testid={`button-remove-vendor-${v.id}`}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">{t("onboarding:phase2.monthlyCost")}</Label>
                <NumericInput value={v.monthlyCost ?? ""} onChange={(val) => updateVendor(v.id, "monthlyCost", val)} prefix="$" placeholder={t("onboarding:phase2.monthlyCostPlaceholder")} testId={`input-vendor-cost-${v.id}`} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">{t("onboarding:phase2.leadsPerMonth")}</Label>
                <NumericInput value={v.leadsPerMonth ?? ""} onChange={(val) => updateVendor(v.id, "leadsPerMonth", val)} suffix={t("onboarding:units.leads")} placeholder={t("onboarding:phase2.leadsPlaceholder")} testId={`input-vendor-leads-${v.id}`} min={0} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">{t("onboarding:phase2.salesPerMonth")}</Label>
                <NumericInput value={v.salesPerMonth ?? ""} onChange={(val) => updateVendor(v.id, "salesPerMonth", val)} suffix={t("onboarding:units.sales")} placeholder={t("onboarding:phase2.salesPlaceholder")} testId={`input-vendor-sales-${v.id}`} min={0} />
              </div>
            </div>
          </div>
        ))}

        <div className="flex gap-2 flex-wrap mt-2">
          {MARKETING_PLATFORM_OPTIONS.slice(0, 5).map((opt) => (
            <Badge
              key={opt.label}
              variant="outline"
              className="cursor-pointer hover:bg-primary/5"
              data-testid={`badge-quick-add-${opt.label.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => {
                if (!vendors.find((v) => v.name === opt.label)) {
                  onChange({ vendors: [...vendors, { id: crypto.randomUUID(), name: opt.label, category: "marketing", monthlyCost: "" }] });
                }
              }}
            >
              + {opt.label}
            </Badge>
          ))}
        </div>
      </div>

      <Separator />

      <div className="space-y-3">
        <Label className="text-base">{t("onboarding:phase2.teamBreakdown")}</Label>
        <div className="grid grid-cols-2 gap-3">
          {employees.map((e) => (
            <div key={e.role} className="flex items-center justify-between gap-3">
              <Label className="text-sm text-muted-foreground">{EMPLOYEE_ROLE_KEYS[e.role] ? t(EMPLOYEE_ROLE_KEYS[e.role]) : e.role}</Label>
              <NumericInput value={e.count} onChange={(v) => updateEmployee(e.role, typeof v === "number" ? v : 0)} suffix={t("onboarding:units.people")} testId={`input-employee-${e.role}`} min={0} />
            </div>
          ))}
        </div>
      </div>

      {totalMonthly > 0 && (
        <InsightCard
          title={t("onboarding:insight.monthlyVendorOverhead")}
          items={[
            { label: t("onboarding:insight.totalVendorCost"), value: `$${totalMonthly.toLocaleString()}` },
            { label: t("onboarding:insight.costPerUnitSold"), value: salesPerMonth > 0 ? `$${Math.round(totalMonthly / salesPerMonth).toLocaleString()}` : "—" },
            { label: t("onboarding:insight.annualVendorSpend"), value: `$${(totalMonthly * 12).toLocaleString()}` },
            { label: t("onboarding:insight.vendorCostItems"), value: t("onboarding:insight.platforms", { count: vendors.length }) },
          ]}
        />
      )}

      {/* ── Live Marketing Analytics ──────────────────────────────────────── */}
      {(() => {
        const mktVendors = vendors.filter((v) => !v.category || v.category === "marketing");
        const totalLeads = mktVendors.reduce((s, v) => s + (typeof v.leadsPerMonth === "number" ? v.leadsPerMonth : 0), 0);
        const totalSales = mktVendors.reduce((s, v) => s + (typeof v.salesPerMonth === "number" ? v.salesPerMonth : 0), 0);
        const totalSpend = mktVendors.reduce((s, v) => s + (typeof v.monthlyCost === "number" ? v.monthlyCost : 0), 0);
        // Show 0.0% when leads exist but no sales yet; only — when no leads at all
        const blendedConv = totalLeads > 0 ? ((totalSales / totalLeads) * 100).toFixed(1) : null;
        const blendedCpl = totalLeads > 0 && totalSpend > 0 ? Math.round(totalSpend / totalLeads) : null;
        const blendedCps = totalSales > 0 && totalSpend > 0 ? Math.round(totalSpend / totalSales) : null;
        // Show analytics card whenever at least one marketing vendor exists (even spend-only)
        const hasAny = mktVendors.length > 0;
        if (!hasAny) return null;

        return (
          <div className="mt-4 p-4 rounded-xl bg-chart-1/8 border border-chart-1/20" data-testid="card-marketing-analytics">
            <div className="flex items-center gap-2 mb-3">
              <BarChart3 className="w-4 h-4 text-chart-1" />
              <p className="text-sm font-semibold text-chart-1">{t("onboarding:phase2.marketingFunnelAnalytics")}</p>
              <span className="text-xs text-muted-foreground ml-auto">{t("onboarding:phase2.updatesLive")}</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
              <div>
                <p className="text-xs text-muted-foreground">{t("onboarding:phase2.totalLeadsPerMonth")}</p>
                <p className="text-sm font-bold tabular-nums" data-testid="analytics-total-leads">{totalLeads > 0 ? totalLeads.toLocaleString() : "—"}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{t("onboarding:phase2.totalSalesPerMonth")}</p>
                <p className="text-sm font-bold tabular-nums" data-testid="analytics-total-sales">{totalSales > 0 ? totalSales.toLocaleString() : "—"}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{t("onboarding:phase2.blendedConversion")}</p>
                <p className="text-sm font-bold tabular-nums" data-testid="analytics-conversion">{blendedConv !== null ? `${blendedConv}%` : "—"}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{t("onboarding:phase2.blendedCpl")}</p>
                <p className="text-sm font-bold tabular-nums" data-testid="analytics-cpl">{blendedCpl !== null ? `$${blendedCpl.toLocaleString()}` : "—"}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{t("onboarding:phase2.blendedCps")}</p>
                <p className="text-sm font-bold tabular-nums" data-testid="analytics-cps">{blendedCps !== null ? `$${blendedCps.toLocaleString()}` : "—"}</p>
              </div>
            </div>
            {mktVendors.filter((v) => v.name && (typeof v.leadsPerMonth === "number" || typeof v.salesPerMonth === "number")).length > 0 && (
              <div className="space-y-1.5">
                <p className="text-xs font-medium text-muted-foreground mb-1">{t("onboarding:phase2.perChannelEfficiency")}</p>
                {mktVendors.filter((v) => v.name).map((v) => {
                  const leads = typeof v.leadsPerMonth === "number" ? v.leadsPerMonth : 0;
                  const sales = typeof v.salesPerMonth === "number" ? v.salesPerMonth : 0;
                  const spend = typeof v.monthlyCost === "number" ? v.monthlyCost : 0;
                  const cpl = leads > 0 && spend > 0 ? Math.round(spend / leads) : null;
                  const cps = sales > 0 && spend > 0 ? Math.round(spend / sales) : null;
                  return (
                    <div key={v.id} className="flex items-center justify-between text-xs py-1 border-b border-chart-1/10 last:border-0" data-testid={`analytics-vendor-${v.id}`}>
                      <span className="text-foreground font-medium truncate max-w-[120px]">{v.name}</span>
                      <span className="text-muted-foreground tabular-nums">CPL: {cpl !== null ? `$${cpl.toLocaleString()}` : "—"}</span>
                      <span className="text-muted-foreground tabular-nums">CPS: {cps !== null ? `$${cps.toLocaleString()}` : "—"}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}

function Phase3({ data, onChange }: { data: PhaseData; onChange: (d: Partial<PhaseData>) => void }) {
  const { t } = useTranslation(["onboarding", "common"]);
  const invType = (data.inventoryType || "used_retail") as DealerInventoryType;
  const bench = getBenchmarks(invType);

  const lendingFields = [
    { key: "lendingPrimePct", label: t("onboarding:phase3.prime"), color: "emerald" },
    { key: "lendingSubprimePct", label: t("onboarding:phase3.subPrime"), color: "blue" },
    { key: "lendingBhphPct", label: t("onboarding:phase3.bhph"), color: "amber" },
    { key: "lendingCashPct", label: t("onboarding:phase3.cash"), color: "violet" },
  ];

  const lendingValues: Record<string, number> = {
    lendingPrimePct: data.lendingPrimePct ?? bench.lendingPrimePct,
    lendingSubprimePct: data.lendingSubprimePct ?? bench.lendingSubprimePct,
    lendingBhphPct: data.lendingBhphPct ?? bench.lendingBhphPct,
    lendingCashPct: data.lendingCashPct ?? bench.lendingCashPct,
  };

  const salesPerMonth = typeof data.avgSalesPerMonth === "number" ? data.avgSalesPerMonth : null;
  const bhphPct = lendingValues.lendingBhphPct;

  return (
    <div className="space-y-6">
      <SectionContext text={t("onboarding:phase3.context")} />

      <div className="space-y-4">
        <div className="space-y-1.5">
          <FieldTooltip content={t("onboarding:phase3.monthlyUnitSalesTip")}>
            <Label className="flex items-center gap-1.5 cursor-help">{t("onboarding:phase3.monthlyUnitSales")} <Info className="w-3.5 h-3.5 text-muted-foreground" /></Label>
          </FieldTooltip>
          <NumericInput value={data.avgSalesPerMonth ?? ""} onChange={(v) => onChange({ avgSalesPerMonth: v })} suffix={t("onboarding:units.unitsPerMo")} placeholder={t("onboarding:phase1.eg", { value: bench.avgSalesPerMonth })} testId="input-avgSalesPerMonth" min={1} />
        </div>

        <Separator />

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-base">{t("onboarding:phase3.lendingMix")}</Label>
            <Badge variant="secondary" className="text-xs">{t("onboarding:phase3.mustTotal100")}</Badge>
          </div>
          <PercentSliders
            fields={lendingFields}
            values={lendingValues}
            onChange={(key, val) => onChange({ [key]: val } as Partial<PhaseData>)}
          />
        </div>
      </div>

      {salesPerMonth && (
        <InsightCard
          title={t("onboarding:insight.volumeMixBreakdown")}
          items={[
            { label: t("onboarding:insight.annualizedVolume"), value: t("onboarding:insight.unitsPerYr", { value: (salesPerMonth * 12).toLocaleString() }) },
            { label: t("onboarding:insight.primeSubprimeUnits"), value: t("onboarding:insight.unitsPerMoValue", { value: Math.round(salesPerMonth * (lendingValues.lendingPrimePct + lendingValues.lendingSubprimePct) / 100) }) },
            { label: t("onboarding:insight.bhphUnitsPerMo"), value: t("onboarding:insight.unitsValue", { value: Math.round(salesPerMonth * lendingValues.lendingBhphPct / 100) }) },
            {
              label: t("onboarding:insight.capitalExposureFlag"),
              value: bhphPct >= 30 ? t("onboarding:insight.highBhph") : bhphPct >= 15 ? t("onboarding:insight.moderateBhph") : t("onboarding:insight.lowBhph"),
            },
          ]}
        />
      )}
    </div>
  );
}

function Phase4({ data, onChange }: { data: PhaseData; onChange: (d: Partial<PhaseData>) => void }) {
  const { t } = useTranslation(["onboarding", "common"]);
  const invType = (data.inventoryType || "used_retail") as DealerInventoryType;
  const bench = getBenchmarks(invType);
  const hasFinanceIncome = (data.lendingPrimePct ?? 0) > 0 || (data.lendingSubprimePct ?? 0) > 0;

  const fe = typeof data.avgFrontEndGross === "number" ? data.avgFrontEndGross : 0;
  const be = typeof data.avgBackEndGross === "number" ? data.avgBackEndGross : 0;
  const fixed = typeof data.avgFixedCosts === "number" ? data.avgFixedCosts : 0;
  const variable = typeof data.avgVariableCostPerUnit === "number" ? data.avgVariableCostPerUnit : 0;
  const sales = typeof data.avgSalesPerMonth === "number" ? data.avgSalesPerMonth : bench.avgSalesPerMonth;

  const recon = typeof data.avgReconCost === "number" ? data.avgReconCost : 0;
  const transport = typeof data.avgTransportFee === "number" ? data.avgTransportFee : 0;

  const totalGross = fe + be;
  // Per-unit variable costs only (recon + transport are per-unit costs too)
  const perUnitVariable = variable + recon + transport;
  const totalCostPerUnit = perUnitVariable + (fixed / Math.max(sales, 1));
  // True per-unit gross margin: gross minus per-unit costs (no fixed allocation)
  const grossMarginPerUnit = totalGross - perUnitVariable;
  const breakeven = fixed > 0 && grossMarginPerUnit > 0 ? Math.ceil(fixed / grossMarginPerUnit) : null;

  return (
    <div className="space-y-6">
      <SectionContext text={t("onboarding:phase4.context")} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="space-y-1.5">
          <FieldTooltip content={t("onboarding:phase4.avgFrontEndGrossTip", { invType, value: bench.avgFrontEndGross.toLocaleString() })}>
            <Label className="flex items-center gap-1.5 cursor-help">{t("onboarding:phase4.avgFrontEndGross")} <Info className="w-3.5 h-3.5 text-muted-foreground" /></Label>
          </FieldTooltip>
          <NumericInput value={data.avgFrontEndGross ?? ""} onChange={(v) => onChange({ avgFrontEndGross: v })} prefix="$" placeholder={t("onboarding:phase1.eg", { value: bench.avgFrontEndGross.toLocaleString() })} testId="input-avgFrontEndGross" />
        </div>

        {hasFinanceIncome && (
          <div className="space-y-1.5">
            <FieldTooltip content={t("onboarding:phase4.avgBackEndGrossTip", { value: bench.avgBackEndGross.toLocaleString() })}>
              <Label className="flex items-center gap-1.5 cursor-help">{t("onboarding:phase4.avgBackEndGross")} <Info className="w-3.5 h-3.5 text-muted-foreground" /></Label>
            </FieldTooltip>
            <NumericInput value={data.avgBackEndGross ?? ""} onChange={(v) => onChange({ avgBackEndGross: v })} prefix="$" placeholder={t("onboarding:phase1.eg", { value: bench.avgBackEndGross.toLocaleString() })} testId="input-avgBackEndGross" />
          </div>
        )}

        <div className="space-y-1.5">
          <FieldTooltip content={t("onboarding:phase4.totalMonthlyFixedCostsTip")}>
            <Label className="flex items-center gap-1.5 cursor-help">{t("onboarding:phase4.totalMonthlyFixedCosts")} <Info className="w-3.5 h-3.5 text-muted-foreground" /></Label>
          </FieldTooltip>
          <NumericInput value={data.avgFixedCosts ?? ""} onChange={(v) => onChange({ avgFixedCosts: v })} prefix="$" suffix={t("onboarding:units.perMo")} placeholder={t("onboarding:phase1.eg", { value: bench.avgFixedCosts.toLocaleString() })} testId="input-avgFixedCosts" />
        </div>

        <div className="space-y-1.5">
          <FieldTooltip content={t("onboarding:phase4.variableCostPerUnitTip")}>
            <Label className="flex items-center gap-1.5 cursor-help">{t("onboarding:phase4.variableCostPerUnit")} <Info className="w-3.5 h-3.5 text-muted-foreground" /></Label>
          </FieldTooltip>
          <NumericInput value={data.avgVariableCostPerUnit ?? ""} onChange={(v) => onChange({ avgVariableCostPerUnit: v })} prefix="$" placeholder={t("onboarding:phase1.eg", { value: bench.avgVariableCostPerUnit.toLocaleString() })} testId="input-avgVariableCostPerUnit" />
        </div>

        <div className="space-y-1.5">
          <FieldTooltip content={t("onboarding:phase4.avgReconCostPerUnitTip", { invType, value: bench.avgReconCost.toLocaleString() })}>
            <Label className="flex items-center gap-1.5 cursor-help">{t("onboarding:phase4.avgReconCostPerUnit")} <Info className="w-3.5 h-3.5 text-muted-foreground" /></Label>
          </FieldTooltip>
          <NumericInput value={data.avgReconCost ?? ""} onChange={(v) => onChange({ avgReconCost: v })} prefix="$" placeholder={t("onboarding:phase1.eg", { value: bench.avgReconCost.toLocaleString() })} testId="input-avgReconCost" />
        </div>

        <div className="space-y-1.5">
          <FieldTooltip content={t("onboarding:phase4.avgTransportFeeTip")}>
            <Label className="flex items-center gap-1.5 cursor-help">{t("onboarding:phase4.avgTransportFee")} <Info className="w-3.5 h-3.5 text-muted-foreground" /></Label>
          </FieldTooltip>
          <NumericInput value={data.avgTransportFee ?? ""} onChange={(v) => onChange({ avgTransportFee: v })} prefix="$" placeholder={t("onboarding:phase1.eg", { value: bench.avgTransportFee.toLocaleString() })} testId="input-avgTransportFee" />
        </div>
      </div>

      {typeof data.avgFrontEndGross === "number" && (
        <InsightCard
          title={t("onboarding:insight.financialStructureSnapshot")}
          items={[
            { label: t("onboarding:insight.totalGrossPerUnit"), value: `$${totalGross.toLocaleString()}` },
            { label: t("onboarding:insight.costPerUnitSold"), value: `$${Math.round(totalCostPerUnit).toLocaleString()}` },
            { label: t("onboarding:insight.grossMarginPerUnit"), value: `$${Math.round(grossMarginPerUnit).toLocaleString()}` },
            { label: t("onboarding:insight.breakEvenUnitsPerMo"), value: breakeven !== null ? t("onboarding:insight.breakEvenUnits", { count: breakeven }) : "—" },
          ]}
        />
      )}
    </div>
  );
}

const FLOORPLAN_LENDER_OPTIONS = [
  { name: "NextGear Capital", typicalRateLow: 18, typicalRateHigh: 21 },
  { name: "AFC (Auction Finance Corp)", typicalRateLow: 18, typicalRateHigh: 22 },
  { name: "Westlake Flooring Services", typicalRateLow: 18, typicalRateHigh: 21 },
  { name: "Ally Financial Dealer Floorplan", typicalRateLow: 15, typicalRateHigh: 18 },
  { name: "Capital One Dealer Floorplan", typicalRateLow: 15, typicalRateHigh: 18 },
  { name: "GM Financial Dealer Floorplan", typicalRateLow: 14, typicalRateHigh: 17 },
  { name: "Ford Motor Credit Floorplan", typicalRateLow: 14, typicalRateHigh: 17 },
  { name: "Toyota Financial Services Floorplan", typicalRateLow: 14, typicalRateHigh: 17 },
  { name: "Chase Auto Finance Dealer Floorplan", typicalRateLow: 15, typicalRateHigh: 18 },
  { name: "Bank of America Dealer Services", typicalRateLow: 14, typicalRateHigh: 17 },
  { name: "1st Choice International", typicalRateLow: 19, typicalRateHigh: 23 },
  { name: "DT Acceptance Corp", typicalRateLow: 19, typicalRateHigh: 23 },
  { name: "Kinetic Advantage", typicalRateLow: 18, typicalRateHigh: 21 },
  { name: "ACV Capital", typicalRateLow: 18, typicalRateHigh: 22 },
  { name: "Dealer Financial Services", typicalRateLow: 17, typicalRateHigh: 20 },
  { name: "Local/Regional Bank", typicalRateLow: 12, typicalRateHigh: 16 },
  { name: "Credit Union", typicalRateLow: 11, typicalRateHigh: 15 },
  { name: "Self-Funded (No Floorplan)", typicalRateLow: 0, typicalRateHigh: 0 },
];

interface AiLenderLookupResult {
  isFloorplanLender: boolean;
  lenderName: string;
  typicalAprLow: number | null;
  typicalAprHigh: number | null;
  knownFeeStructure: string | null;
  notes: string | null;
  rejectionReason: string | null;
}

interface LenderChipOption {
  name: string;
  typicalRateLow: number | null;
  typicalRateHigh: number | null;
  fromDb: boolean;
  isSelfFunded: boolean;
}

function Phase6({ data, onChange }: { data: PhaseData; onChange: (d: Partial<PhaseData>) => void }) {
  const { t } = useTranslation(["onboarding", "common"]);
  const invType = (data.inventoryType || "used_retail") as DealerInventoryType;
  const bench = getBenchmarks(invType);

  const sourcingFields = [
    { key: "sourcingAuctionPct", label: t("onboarding:phase6.auction"), color: "blue" },
    { key: "sourcingTradeInPct", label: t("onboarding:phase6.tradeIn"), color: "emerald" },
    { key: "sourcingPrivatePartyPct", label: t("onboarding:phase6.privateParty"), color: "amber" },
    { key: "sourcingDealerToDealerPct", label: t("onboarding:phase6.dealerToDealer"), color: "violet" },
  ];

  const sourcingValues: Record<string, number> = {
    sourcingAuctionPct: data.sourcingAuctionPct ?? bench.sourcingAuctionPct,
    sourcingTradeInPct: data.sourcingTradeInPct ?? bench.sourcingTradeInPct,
    sourcingPrivatePartyPct: data.sourcingPrivatePartyPct ?? bench.sourcingPrivatePartyPct,
    sourcingDealerToDealerPct: data.sourcingDealerToDealerPct ?? bench.sourcingDealerToDealerPct,
  };

  const reconDays = typeof data.avgReconCost === "number" ? Math.ceil(data.avgReconCost / 300) : 7;
  const daysToFrontline = typeof data.avgDaysPurchaseToFrontline === "number" ? data.avgDaysPurchaseToFrontline : bench.avgDaysPurchaseToFrontline;
  const rate = typeof data.avgFloorplanRate === "number" ? data.avgFloorplanRate : bench.avgFloorplanRate;
  const avgUnitValue = typeof data.avgVehicleValue === "number" ? data.avgVehicleValue : 14000;
  const holdingCostDuringRecon = avgUnitValue * (rate / 100) / 365 * daysToFrontline;

  return (
    <div className="space-y-6">
      <SectionContext text={t("onboarding:phase6.context")} />

      <div className="space-y-4">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-base">{t("onboarding:phase6.sourcingMix")}</Label>
            <Badge variant="secondary" className="text-xs">{t("onboarding:phase6.mustTotal100")}</Badge>
          </div>
          <PercentSliders
            fields={sourcingFields}
            values={sourcingValues}
            onChange={(key, val) => onChange({ [key]: val } as Partial<PhaseData>)}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <AuctionLanesInput
            lanes={data.primaryAuctionLanes ?? []}
            onChange={(lanes) => onChange({ primaryAuctionLanes: lanes })}
          />


          <div className="space-y-1.5">
            <FieldTooltip content={t("onboarding:phase6.daysPurchaseToFrontlineTip")}>
              <Label className="flex items-center gap-1.5 cursor-help">{t("onboarding:phase6.daysPurchaseToFrontline")} <Info className="w-3.5 h-3.5 text-muted-foreground" /></Label>
            </FieldTooltip>
            <NumericInput value={data.avgDaysPurchaseToFrontline ?? ""} onChange={(v) => onChange({ avgDaysPurchaseToFrontline: v })} suffix={t("onboarding:units.days")} placeholder={t("onboarding:phase1.eg", { value: bench.avgDaysPurchaseToFrontline })} testId="input-avgDaysPurchaseToFrontline" min={1} max={30} />
          </div>
        </div>
      </div>

    </div>
  );
}


function Phase7({ data, onChange }: { data: PhaseData; onChange: (d: Partial<PhaseData>) => void }) {
  const { t } = useTranslation(["onboarding", "common"]);
  const isBhph = (data.lendingBhphPct ?? 0) >= 10;

  return (
    <div className="space-y-6">
      <SectionContext text={t("onboarding:phase7.context")} />

      <div className="space-y-5">
        <div className="space-y-2">
          <div className="flex items-center gap-1">
            <Label>{t("onboarding:phase7.cashReserves")}</Label>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {["<50k", "50-150k", "150-500k", "500k+"].map((range) => (
              <Button
                key={range}
                variant={data.cashReservesRange === range ? "default" : "outline"}
                size="sm"
                onClick={() => onChange({ cashReservesRange: range })}
                data-testid={`button-reserves-${range}`}
                className={data.cashReservesRange === range ? "w-full" : "w-full bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100 dark:bg-blue-950 dark:border-blue-700 dark:text-blue-300 dark:hover:bg-blue-900"}
              >
                ${range}
              </Button>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <Label>{t("onboarding:phase7.revolvingLoc")}</Label>
            <p className="text-xs text-muted-foreground mt-0.5">{t("onboarding:phase7.revolvingLocDesc")}</p>
          </div>
          <Switch
            checked={data.hasLineOfCredit ?? false}
            onCheckedChange={(v) => onChange({ hasLineOfCredit: v })}
            data-testid="switch-hasLineOfCredit"
          />
        </div>

        {data.hasLineOfCredit && (
          <div className="space-y-2 pl-4 border-l-2 border-primary/30">
            <Label>{t("onboarding:phase7.locLimitRange")}</Label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {["<100k", "100-250k", "250-500k", "500k+"].map((range) => (
                <Button
                  key={range}
                  variant={data.lineOfCreditRange === range ? "default" : "outline"}
                  size="sm"
                  onClick={() => onChange({ lineOfCreditRange: range })}
                  data-testid={`button-loc-${range}`}
                  className={data.lineOfCreditRange === range ? "" : "bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100 dark:bg-blue-950 dark:border-blue-700 dark:text-blue-300 dark:hover:bg-blue-900"}
                >
                  ${range}
                </Button>
              ))}
            </div>
          </div>
        )}

        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-1">
              <Label>{t("onboarding:phase7.nonFloorplanDebt")}</Label>
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">{t("onboarding:phase7.nonFloorplanDebtDesc")}</p>
          </div>
          <Switch
            checked={data.hasNonFloorplanDebt ?? false}
            onCheckedChange={(v) => onChange({ hasNonFloorplanDebt: v })}
            data-testid="switch-hasNonFloorplanDebt"
          />
        </div>

        {data.hasNonFloorplanDebt && (
          <div className="space-y-1.5 pl-4 border-l-2 border-primary/30">
            <Label>{t("onboarding:phase7.monthlyObligation")}</Label>
            <NumericInput value={data.nonFloorplanDebtMonthly ?? ""} onChange={(v) => onChange({ nonFloorplanDebtMonthly: v })} prefix="$" suffix={t("onboarding:units.perMo")} testId="input-nonFloorplanDebtMonthly" />
          </div>
        )}

        {isBhph && (
          <div className="space-y-2">
            <Label>{t("onboarding:phase7.bhphReceivables")}</Label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {["<100k", "100-500k", "500k-2M", "2M+"].map((range) => (
                <Button
                  key={range}
                  variant={data.bhphReceivablesRange === range ? "default" : "outline"}
                  size="sm"
                  onClick={() => onChange({ bhphReceivablesRange: range })}
                  data-testid={`button-bhph-${range}`}
                  className={data.bhphReceivablesRange === range ? "" : "bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100 dark:bg-blue-950 dark:border-blue-700 dark:text-blue-300 dark:hover:bg-blue-900"}
                >
                  ${range}
                </Button>
              ))}
            </div>
          </div>
        )}
      </div>

      {data.cashReservesRange && (
        <div className="p-4 rounded-xl bg-muted/40 border">
          <div className="flex items-center gap-2 mb-2">
            <PiggyBank className="w-4 h-4 text-primary" />
            <p className="text-sm font-semibold">{t("onboarding:phase7.liquidityAssessment")}</p>
          </div>
          <p className="text-sm text-muted-foreground">
            {data.cashReservesRange === "<50k"
              ? t("onboarding:phase7.liquidityLow")
              : data.cashReservesRange === "50-150k"
                ? t("onboarding:phase7.liquidityModerate")
                : data.cashReservesRange === "150-500k"
                  ? t("onboarding:phase7.liquiditySolid")
                  : t("onboarding:phase7.liquidityStrong")}
          </p>
        </div>
      )}

      <div className="p-4 rounded-xl border border-primary/30 bg-primary/5">
        <div className="flex items-start gap-3">
          <FileText className="w-5 h-5 text-primary mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-foreground">{t("onboarding:phase7.balanceSheetTitle")}</p>
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
              {t("onboarding:phase7.balanceSheetDesc")}
            </p>
            <div className="mt-3 flex items-center gap-3 flex-wrap">
              <a
                href="/financial-statements"
                className="inline-flex items-center gap-1.5 text-xs font-medium text-primary bg-primary/10 hover:bg-primary/20 rounded-md px-3 py-1.5 transition-colors"
                data-testid="link-phase7-go-to-balance-sheet"
              >
                <FileText className="w-3.5 h-3.5" /> {t("onboarding:phase7.openBalanceSheet")}
              </a>
              <span className="text-[10px] text-muted-foreground">
                {t("onboarding:phase7.balanceSheetSupport")}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


// ─── Inline tags input (for lead sources) ────────────────────────────────────

function TagsInput({ tags, onChange, placeholder }: { tags: string[]; onChange: (t: string[]) => void; placeholder?: string }) {
  const { t: translate } = useTranslation(["onboarding", "common"]);
  const [draft, setDraft] = useState("");
  const addTag = () => {
    const t = draft.trim();
    if (!t || tags.includes(t)) return;
    onChange([...tags, t]);
    setDraft("");
  };
  return (
    <div className="space-y-1.5">
      <div className="flex gap-2">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
          placeholder={placeholder ?? translate("onboarding:tags.addItem")}
          data-testid="input-tags-draft"
        />
        <Button type="button" variant="outline" size="icon" onClick={addTag} disabled={!draft.trim()} aria-label={translate("onboarding:tags.add")} data-testid="button-add-tag">
          <Plus className="w-4 h-4" />
        </Button>
      </div>
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pt-1">
          {tags.map((tag, i) => (
            <span key={i} className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary border border-primary/20">
              {tag}
              <button type="button" onClick={() => onChange(tags.filter((_, j) => j !== i))} className="ml-0.5 hover:text-destructive transition-colors" aria-label={`Remove ${tag}`} data-testid={`button-remove-tag-${i}`}>
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Inline missing-fields form ───────────────────────────────────────────────

function InlineMissingFieldsForm({
  missingFields,
  phaseData,
  onSave,
  isPending,
  onClose,
  editAllMode,
  onUnsavedCountChange,
}: {
  missingFields: Array<{ key: string; label: string }>;
  phaseData: PhaseData;
  onSave: (values: Record<string, any>) => void;
  isPending: boolean;
  onClose?: () => void;
  editAllMode?: boolean;
  onUnsavedCountChange?: (count: number) => void;
}) {
  const { t } = useTranslation(["onboarding", "common"]);
  const fieldsWithMeta = missingFields.filter((f) => MISSING_FIELD_META[f.key]);

  const DRAFT_KEY = editAllMode ? "phase9_edit_all_draft" : "phase9_missing_fields_draft";

  // Benchmark defaults keyed by field name, used to pre-populate empty fields
  const benchmarks = getBenchmarks(phaseData.inventoryType as string | undefined);

  function getBenchmarkDefault(key: string): string | number | undefined {
    const v = benchmarks[key as keyof typeof benchmarks];
    if (typeof v === "number" || typeof v === "string") return v;
    return undefined;
  }

  // Initialize form state: prefer sessionStorage draft, then phaseData, then benchmark defaults
  const [values, setValuesRaw] = useState<Record<string, any>>(() => {
    let draft: Record<string, any> = {};
    try {
      const stored = sessionStorage.getItem(DRAFT_KEY);
      if (stored) draft = JSON.parse(stored);
    } catch {}
    const init: Record<string, any> = {};
    for (const f of fieldsWithMeta) {
      if (draft[f.key] != null && draft[f.key] !== "") {
        init[f.key] = draft[f.key];
      } else {
        const v = (phaseData as Record<string, any>)[f.key];
        if (v != null && v !== "") {
          init[f.key] = v;
        } else {
          const bv = getBenchmarkDefault(f.key);
          if (bv != null && bv !== "") init[f.key] = bv;
        }
      }
    }
    return init;
  });

  const setValues = (updater: Record<string, any> | ((prev: Record<string, any>) => Record<string, any>)) => {
    setValuesRaw((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      try { sessionStorage.setItem(DRAFT_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  };

  // Track the saved values so we can detect which fields have been changed
  const [savedValues, setSavedValues] = useState<Record<string, any>>(() => {
    let draft: Record<string, any> = {};
    try {
      const stored = sessionStorage.getItem(DRAFT_KEY);
      if (stored) draft = JSON.parse(stored);
    } catch {}
    const init: Record<string, any> = {};
    for (const f of fieldsWithMeta) {
      if (draft[f.key] != null && draft[f.key] !== "") {
        init[f.key] = draft[f.key];
      } else {
        const v = (phaseData as Record<string, any>)[f.key];
        if (v != null && v !== "") {
          init[f.key] = v;
        } else {
          const bv = getBenchmarkDefault(f.key);
          if (bv != null && bv !== "") init[f.key] = bv;
        }
      }
    }
    return init;
  });

  // When missingFields or phaseData changes (e.g. after a partial save), merge saved
  // values with any existing sessionStorage draft so corrections are always reflected.
  useEffect(() => {
    let draft: Record<string, any> = {};
    try {
      const stored = sessionStorage.getItem(DRAFT_KEY);
      if (stored) draft = JSON.parse(stored);
    } catch {}
    const next: Record<string, any> = {};
    for (const f of missingFields.filter((mf) => MISSING_FIELD_META[mf.key])) {
      if (draft[f.key] != null && draft[f.key] !== "") {
        next[f.key] = draft[f.key];
      } else {
        const v = (phaseData as Record<string, any>)[f.key];
        if (v != null && v !== "") {
          next[f.key] = v;
        } else {
          const bv = getBenchmarkDefault(f.key);
          if (bv != null && bv !== "") next[f.key] = bv;
        }
      }
    }
    setValuesRaw(next);
    setSavedValues(next);
  }, [missingFields, phaseData]);

  // Compute which fields have changed from their saved values and would be submitted.
  // Fields cleared to empty are excluded because handleSave skips empty values.
  const changedKeys = new Set<string>(
    fieldsWithMeta
      .map((f) => f.key)
      .filter((k) => {
        const cur = values[k];
        const orig = savedValues[k];
        // Skip if the current value is empty (won't be in the saved payload)
        const isEmpty = cur == null || cur === "" || (Array.isArray(cur) && cur.length === 0);
        if (isEmpty) return false;
        if (Array.isArray(cur) || Array.isArray(orig)) {
          return JSON.stringify(cur ?? []) !== JSON.stringify(orig ?? []);
        }
        const curNorm = cur == null ? "" : cur;
        const origNorm = orig == null ? "" : orig;
        return curNorm !== origNorm;
      })
  );

  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  // Report unsaved count to parent so it can guard other close paths (e.g. toggle button)
  useEffect(() => {
    onUnsavedCountChange?.(changedKeys.size);
  }, [changedKeys.size]);

  const handleCloseRequest = () => {
    if (changedKeys.size > 0) {
      setShowCloseConfirm(true);
    } else {
      onClose?.();
    }
  };

  if (fieldsWithMeta.length === 0) return null;

  // Group fields by category
  const groups: Record<string, Array<{ key: string; label: string }>> = {};
  for (const f of fieldsWithMeta) {
    const g = MISSING_FIELD_META[f.key].group;
    if (!groups[g]) groups[g] = [];
    groups[g].push(f);
  }

  const handleSave = () => {
    // Only submit fields that have been filled
    const payload: Record<string, any> = {};
    for (const [k, v] of Object.entries(values)) {
      if (v == null || v === "" || (Array.isArray(v) && v.length === 0)) continue;
      payload[k] = v;
    }
    if (Object.keys(payload).length > 0) {
      try { sessionStorage.removeItem(DRAFT_KEY); } catch {}
      onSave(payload);
    }
  };

  const hasAnyValue = Object.values(values).some((v) =>
    v != null && v !== "" && !(Array.isArray(v) && v.length === 0)
  );

  return (
    <div className="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50/30 dark:bg-amber-900/10 p-5 space-y-5" data-testid="inline-missing-fields-form">
      <div className="flex items-center gap-2">
        <Pencil className="w-4 h-4 text-amber-600 dark:text-amber-400" />
        <p className="text-sm font-semibold">{editAllMode ? t("onboarding:inlineForm.editProfileFields") : t("onboarding:inlineForm.completeProfile")}</p>
        {!editAllMode && (
          <Badge variant="outline" className="text-xs border-amber-300 text-amber-700 dark:text-amber-400">
            {t("onboarding:inlineForm.fieldsMissing", { count: fieldsWithMeta.length })}
          </Badge>
        )}
        {editAllMode && onClose && (
          <button
            onClick={handleCloseRequest}
            className="ml-auto text-muted-foreground hover:text-foreground transition-colors"
            aria-label={t("onboarding:inlineForm.closeEditForm")}
            data-testid="button-close-edit-all-fields"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
      <p className="text-xs text-muted-foreground">
        {editAllMode
          ? t("onboarding:inlineForm.editAllDesc")
          : t("onboarding:inlineForm.completeDesc")}
      </p>

      {Object.entries(groups).map(([groupName, fields]) => (
        <div key={groupName} className="space-y-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground border-b pb-1">{t(`onboarding:meta.group.${groupName}`, { defaultValue: groupName })}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {fields.map((f) => {
              const meta = MISSING_FIELD_META[f.key];
              const value = values[f.key];
              const isChanged = changedKeys.has(f.key);
              return (
                <div
                  key={f.key}
                  className={`space-y-1.5 ${meta.type === "auction_lanes" || meta.type === "tags" ? "sm:col-span-2" : ""} ${isChanged ? "pl-2 border-l-2 border-amber-400 dark:border-amber-500" : ""}`}
                  data-testid={isChanged ? `field-changed-${f.key}` : undefined}
                >
                  <Label className="text-sm flex items-center gap-1.5">
                    {t(`onboarding:meta.field.${f.key}`, { defaultValue: f.label })}
                    {isChanged && (
                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" aria-label={t("onboarding:inlineForm.unsavedChange")} />
                    )}
                  </Label>
                  {meta.type === "number" && (
                    <NumericInput
                      value={typeof value === "number" ? value : ""}
                      onChange={(v) => setValues((prev) => ({ ...prev, [f.key]: v }))}
                      prefix={meta.prefix}
                      suffix={meta.suffix}
                      placeholder={meta.placeholder}
                      min={meta.min}
                      max={meta.max}
                      testId={`inline-input-${f.key}`}
                    />
                  )}
                  {meta.type === "text" && (
                    <Input
                      value={typeof value === "string" ? value : ""}
                      onChange={(e) => setValues((prev) => ({ ...prev, [f.key]: e.target.value }))}
                      placeholder={meta.placeholder}
                      data-testid={`inline-input-${f.key}`}
                    />
                  )}
                  {meta.type === "select" && (
                    <Select
                      value={typeof value === "string" ? value : ""}
                      onValueChange={(v) => setValues((prev) => ({ ...prev, [f.key]: v }))}
                    >
                      <SelectTrigger data-testid={`inline-select-${f.key}`}>
                        <SelectValue placeholder={t("onboarding:inlineForm.select")} />
                      </SelectTrigger>
                      <SelectContent>
                        {(meta.options ?? []).map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>{f.key === "inventoryType" ? t(`onboarding:meta.invOption.${opt.value}`, { defaultValue: opt.label }) : opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  {meta.type === "auction_lanes" && (
                    <AuctionLanesInput
                      lanes={Array.isArray(value) ? value : []}
                      onChange={(lanes) => setValues((prev) => ({ ...prev, [f.key]: lanes }))}
                    />
                  )}
                  {meta.type === "tags" && (
                    <TagsInput
                      tags={Array.isArray(value) ? value : []}
                      onChange={(tags) => setValues((prev) => ({ ...prev, [f.key]: tags }))}
                      placeholder={meta.placeholder}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}

      <Button
        onClick={handleSave}
        disabled={isPending || !hasAnyValue}
        className="gap-2"
        data-testid="button-save-missing-fields"
      >
        {isPending ? (
          <><RefreshCw className="w-4 h-4 animate-spin" /> {t("onboarding:inlineForm.saving")}</>
        ) : changedKeys.size > 0 ? (
          <><CheckCircle2 className="w-4 h-4" /> {t("onboarding:inlineForm.saveChanges", { count: changedKeys.size })}</>
        ) : (
          <><CheckCircle2 className="w-4 h-4" /> {t("onboarding:inlineForm.saveAndUpdate")}</>
        )}
      </Button>

      <AlertDialog open={showCloseConfirm} onOpenChange={setShowCloseConfirm}>
        <AlertDialogContent data-testid="dialog-unsaved-changes">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("onboarding:inlineForm.discardTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("onboarding:inlineForm.discardDesc", { count: changedKeys.size })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-close-confirm-cancel">{t("onboarding:inlineForm.keepEditing")}</AlertDialogCancel>
            <AlertDialogAction
              data-testid="button-close-confirm-discard"
              onClick={() => {
                try { sessionStorage.removeItem(DRAFT_KEY); } catch {}
                setShowCloseConfirm(false);
                onClose?.();
              }}
            >
              {t("onboarding:inlineForm.discardClose")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// All CONFIDENCE_KEY_FIELDS that have inline field metadata (can be edited inline)
const ALL_EDITABLE_FIELDS = CONFIDENCE_KEY_FIELDS.filter((f) => MISSING_FIELD_META[f.key]);

function Phase9({
  data, aiFlags, validating, onValidate, onFlagAction, confidenceScore, confidenceBreakdown, missingFields,
  onSaveMissingFields, savingMissingFields,
}: {
  data: PhaseData;
  aiFlags: AiFlag[];
  validating: boolean;
  onValidate: () => void;
  onFlagAction: (field: string, action: "keep" | "update") => void;
  confidenceScore: number;
  confidenceBreakdown: ConfidenceBreakdown | null;
  missingFields: Array<{ key: string; label: string }>;
  onSaveMissingFields: (values: Record<string, any>) => void;
  savingMissingFields: boolean;
}) {
  const { t } = useTranslation(["onboarding", "common"]);
  const resolvedFlags = aiFlags.filter((f) => f.action !== undefined);
  const unresolvedFlags = aiFlags.filter((f) => f.action === undefined);
  const cleanPhases = [1, 2, 3, 4, 5].filter((p) => !aiFlags.some((f) => f.phase === p));
  const [isEditingAllFields, setIsEditingAllFieldsRaw] = useState<boolean>(() => {
    try { return sessionStorage.getItem("phase9_edit_panel_open") === "true"; } catch { return false; }
  });
  const [editAllUnsavedCount, setEditAllUnsavedCount] = useState(0);
  const [showEditAllUnsavedWarning, setShowEditAllUnsavedWarning] = useState(false);
  const setIsEditingAllFields = (next: boolean | ((prev: boolean) => boolean)) => {
    setIsEditingAllFieldsRaw((prev) => {
      const value = typeof next === "function" ? next(prev) : next;
      try { sessionStorage.setItem("phase9_edit_panel_open", String(value)); } catch {}
      return value;
    });
  };

  return (
    <div className="space-y-6">
      <SectionContext text={t("onboarding:phase9.context")} />

      {aiFlags.length === 0 && !validating && (
        <div className="text-center py-8">
          <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-4">
            <CheckCheck className="w-8 h-8 text-emerald-500" />
          </div>
          <p className="text-lg font-semibold mb-2">{t("onboarding:phase9.everythingGreat")}</p>
          <p className="text-sm text-muted-foreground">{t("onboarding:phase9.everythingGreatDesc")}</p>
          <Button onClick={onValidate} className="mt-4 gap-2" variant="outline" data-testid="button-run-validation">
            <Shield className="w-4 h-4" />
            {t("onboarding:phase9.validateWithAi")}
          </Button>
        </div>
      )}

      {validating && (
        <div className="flex flex-col items-center py-12 gap-4">
          <RefreshCw className="w-8 h-8 text-primary animate-spin" />
          <p className="text-sm text-muted-foreground">{t("onboarding:phase9.aiReviewing")}</p>
        </div>
      )}

      {!validating && aiFlags.length > 0 && (
        <div className="space-y-4">
          {unresolvedFlags.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-500" />
                <p className="text-sm font-semibold">{t("onboarding:phase9.flaggedForReview", { count: unresolvedFlags.length })}</p>
              </div>

              {unresolvedFlags.map((flag) => (
                <div key={flag.field} className="p-4 rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/10 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-sm font-semibold capitalize">{flag.field.replace(/([A-Z])/g, " $1").trim()}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{flag.explanation}</p>
                    </div>
                    <Badge variant="outline" className="text-xs shrink-0 border-amber-300">{t("onboarding:phase9.phaseLabel", { phase: flag.phase })}</Badge>
                  </div>
                  <div className="flex items-center gap-4 text-xs">
                    <div><p className="text-muted-foreground">{t("onboarding:phase9.yourValue")}</p><p className="font-bold">{String(flag.dealerValue)}</p></div>
                    <div><p className="text-muted-foreground">{t("onboarding:phase9.benchmark")}</p><p className="font-medium">{flag.benchmarkRange}</p></div>
                  </div>
                  <div className="flex gap-2 pt-1">
                    <Button size="sm" variant="outline" onClick={() => onFlagAction(flag.field, "keep")} data-testid={`button-keep-${flag.field}`} className="gap-1.5 h-7 text-xs">
                      <Shield className="w-3 h-3" /> {t("onboarding:phase9.keepMyNumber")}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => onFlagAction(flag.field, "update")} data-testid={`button-update-${flag.field}`} className="gap-1.5 h-7 text-xs">
                      {t("onboarding:phase9.update")}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {resolvedFlags.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-muted-foreground">{t("onboarding:phase9.resolved")}</p>
              {resolvedFlags.map((flag) => (
                <div key={flag.field} className="flex items-center justify-between gap-2 p-2.5 rounded-lg bg-muted/30 text-sm">
                  <span className="capitalize">{flag.field.replace(/([A-Z])/g, " $1").trim()}</span>
                  <Badge variant={flag.action === "keep" ? "secondary" : "outline"} className="text-xs">
                    {flag.action === "keep" ? t("onboarding:phase9.kept") : t("onboarding:phase9.willUpdate")}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {cleanPhases.length > 0 && !validating && aiFlags.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-muted-foreground">{t("onboarding:phase9.cleanPhases")}</p>
          <div className="flex flex-wrap gap-2">
            {cleanPhases.map((p) => (
              <Badge key={p} variant="secondary" className="gap-1 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800">
                <CheckCircle2 className="w-3 h-3" /> {t("onboarding:phase9.phaseChip", { phase: p })}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {confidenceBreakdown && !validating && (
        <div className="rounded-xl border bg-muted/30 p-5 space-y-4" data-testid="confidence-breakdown-panel">
          <div className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-primary" />
            <p className="text-sm font-semibold">{t("onboarding:phase9.confidenceBreakdown")}</p>
            <button
              onClick={() => {
                if (isEditingAllFields && editAllUnsavedCount > 0) {
                  setShowEditAllUnsavedWarning(true);
                } else {
                  setIsEditingAllFields((prev) => !prev);
                }
              }}
              className="ml-auto flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors px-2 py-1 rounded-md hover:bg-muted"
              data-testid="button-edit-all-fields"
              aria-label={t("onboarding:phase9.editAllFields")}
            >
              <Pencil className="w-3.5 h-3.5" />
              {isEditingAllFields ? t("onboarding:phase9.closeEditor") : t("onboarding:phase9.editFields")}
            </button>
          </div>

          <div className="space-y-3">
            <div className="space-y-1">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{t("onboarding:phase9.overallScore")}</span>
                <span className={`font-bold tabular-nums ${confidenceScore >= 70 ? "text-emerald-500" : confidenceScore >= 40 ? "text-amber-500" : "text-muted-foreground"}`} data-testid="breakdown-overall-score">{confidenceScore}%</span>
              </div>
              <Progress value={confidenceScore} className="h-2" />
            </div>

            <div className="grid gap-2">
              <div className="flex items-center justify-between text-sm p-2.5 rounded-lg bg-background border">
                <div>
                  <p className="font-medium">{t("onboarding:phase9.fieldCompleteness")}</p>
                  <p className="text-xs text-muted-foreground">{t("onboarding:phase9.fieldsFilled", { filled: confidenceBreakdown.filledCount, total: confidenceBreakdown.totalFields })}</p>
                </div>
                <span className="font-bold tabular-nums" data-testid="breakdown-base-score">{confidenceBreakdown.baseScore}/70</span>
              </div>
              <div className="flex items-center justify-between text-sm p-2.5 rounded-lg bg-background border">
                <div>
                  <p className="font-medium">{t("onboarding:phase9.bankStatementBonus")}</p>
                  <p className="text-xs text-muted-foreground">{t("onboarding:phase9.bankStatementBonusDesc")}</p>
                </div>
                <span className="font-bold tabular-nums" data-testid="breakdown-statement-bonus">{confidenceBreakdown.statementBonus}/20</span>
              </div>
              <div className="flex items-center justify-between text-sm p-2.5 rounded-lg bg-background border">
                <div>
                  <p className="font-medium">{t("onboarding:phase9.aiValidationBonus")}</p>
                  <p className="text-xs text-muted-foreground">{t("onboarding:phase9.aiValidationBonusDesc")}</p>
                </div>
                <span className="font-bold tabular-nums" data-testid="breakdown-validation-bonus">{confidenceBreakdown.validationBonus}/10</span>
              </div>
            </div>
          </div>

          {missingFields.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t("onboarding:phase9.missingFieldsTitle")}</p>
              <p className="text-xs text-muted-foreground">
                {t("onboarding:phase9.missingFieldsDesc", { count: missingFields.length, points: Math.round((missingFields.length / confidenceBreakdown.totalFields) * 70) })}
              </p>
            </div>
          )}
        </div>
      )}

      {isEditingAllFields && !validating && (
        <InlineMissingFieldsForm
          missingFields={ALL_EDITABLE_FIELDS}
          phaseData={data}
          onSave={onSaveMissingFields}
          isPending={savingMissingFields}
          editAllMode
          onClose={() => setIsEditingAllFields(false)}
          onUnsavedCountChange={setEditAllUnsavedCount}
        />
      )}

      {!isEditingAllFields && missingFields.length > 0 && !validating && (
        <InlineMissingFieldsForm
          missingFields={missingFields}
          phaseData={data}
          onSave={onSaveMissingFields}
          isPending={savingMissingFields}
        />
      )}

      <AlertDialog open={showEditAllUnsavedWarning} onOpenChange={setShowEditAllUnsavedWarning}>
        <AlertDialogContent data-testid="dialog-unsaved-changes-toggle">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("onboarding:inlineForm.discardTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("onboarding:inlineForm.discardDesc", { count: editAllUnsavedCount })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-toggle-confirm-cancel">{t("onboarding:inlineForm.keepEditing")}</AlertDialogCancel>
            <AlertDialogAction
              data-testid="button-toggle-confirm-discard"
              onClick={() => {
                try { sessionStorage.removeItem("phase9_edit_all_draft"); } catch {}
                setShowEditAllUnsavedWarning(false);
                setIsEditingAllFields(false);
              }}
            >
              {t("onboarding:inlineForm.discardClose")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─── Phase configs ─────────────────────────────────────────────────────────────

const PHASES = [
  { num: 1, labelKey: "phaseNav.profile", icon: Building2 },
  { num: 2, labelKey: "phaseNav.sales", icon: TrendingUpIcon },
  { num: 3, labelKey: "phaseNav.dmsMarketingTeam", icon: ShoppingCart },
  { num: 4, labelKey: "phaseNav.financials", icon: DollarSign },
  { num: 5, labelKey: "phaseNav.sourcing", icon: Truck },
];

function TrendingUpIcon(props: any) { return <BarChart3 {...props} />; }

// ─── Entry Screen ─────────────────────────────────────────────────────────────

function EntryScreen({ onChoose }: {
  onChoose: (method: "manual" | "statement", statementId?: string, uploadedFields?: Record<string, any>) => void;
  onUploadInBackground?: (file: File) => void;
}) {
  const { t } = useTranslation(["onboarding", "common"]);
  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-background p-6">
      <div className="max-w-2xl w-full space-y-8">
        <div className="text-center space-y-3">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto">
            <Sparkles className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-3xl font-bold">{t("onboarding:entry.welcome")}</h1>
          <p className="text-muted-foreground text-base leading-relaxed max-w-lg mx-auto">
            {t("onboarding:entry.intro")}
          </p>
        </div>

        <div className="pt-2 space-y-4">
          <button
            onClick={() => onChoose("manual")}
            data-testid="button-entry-manual"
            className="w-full flex items-center justify-center gap-2 p-5 rounded-2xl bg-primary text-primary-foreground font-semibold text-base hover:bg-primary/90 transition-all"
          >
            {t("onboarding:entry.cta")}
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Completion / Baseline Report ─────────────────────────────────────────────

function CompletionReport({
  data, profile, vendors, confidenceScore, onGoToDashboard,
}: {
  data: PhaseData;
  profile: any;
  vendors: any[];
  confidenceScore: number;
  onGoToDashboard: () => void;
}) {
  const { t } = useTranslation(["onboarding", "common"]);
  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-background p-6">
      <div className="max-w-3xl w-full space-y-8">
        <div className="text-center space-y-3">
          <div className="w-16 h-16 rounded-2xl bg-emerald-500/10 flex items-center justify-center mx-auto">
            <CheckCircle2 className="w-8 h-8 text-emerald-500" />
          </div>
          <h1 className="text-3xl font-bold">{t("onboarding:completion.allSet")}</h1>
          <p className="text-muted-foreground">{t("onboarding:completion.completeFor", { name: data.dealershipName || t("onboarding:completion.yourDealership") })}</p>
        </div>

        <div className="rounded-xl border bg-muted/30 p-5 space-y-4" data-testid="seeding-disclosure-panel">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-primary" />
            <p className="text-sm font-semibold">{t("onboarding:completion.nowActivated")}</p>
          </div>
          <div className="space-y-3">
            {[
              {
                icon: Building2,
                key: "profileGoals",
                title: t("onboarding:completion.profileGoalsTitle"),
                description: t("onboarding:completion.profileGoalsDesc"),
              },
              {
                icon: BarChart3,
                key: "analytics",
                title: t("onboarding:completion.analyticsTitle"),
                description: t("onboarding:completion.analyticsDesc"),
              },
            ].map((item) => (
              <div key={item.key} className="flex gap-3 p-3 rounded-lg bg-background border" data-testid={`activated-item-${item.key}`}>
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                  <item.icon className="w-4 h-4 text-primary" />
                </div>
                <div>
                  <p className="text-sm font-medium">{item.title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-col items-center gap-3">
          <Button size="lg" onClick={onGoToDashboard} data-testid="button-go-to-dashboard" className="gap-2">
            {t("onboarding:completion.goToDashboard")} <ArrowRight className="w-4 h-4" />
          </Button>
          <p className="text-xs text-muted-foreground">{t("onboarding:completion.dataSaved")}</p>
        </div>
      </div>
    </div>
  );
}

// ─── Main Wizard ───────────────────────────────────────────────────────────────

export default function OnboardingPage() {
  const { t } = useTranslation(["onboarding", "common"]);
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const qc = useQueryClient();
  const isReviewMode = new URLSearchParams(window.location.search).get("mode") === "review";

  const [step, setStep] = useState<"entry" | "wizard" | "complete">("entry");
  const [currentPhase, setCurrentPhase] = useState(1);
  const [phaseData, setPhaseData] = useState<PhaseData>({});
  const [aiFlags, setAiFlags] = useState<AiFlag[]>([]);
  const [validating, setValidating] = useState(false);
  const [confidenceScore, setConfidenceScore] = useState(0);
  const [bgExtracting, setBgExtracting] = useState(false);
  const [confidenceBreakdown, setConfidenceBreakdown] = useState<ConfidenceBreakdown | null>(null);
  const [missingFields, setMissingFields] = useState<Array<{ key: string; label: string }>>([]);
  const [completedReport, setCompletedReport] = useState<{ profile: any; vendors: any[] } | null>(null);

  const { data: progressData, isLoading: loadingProgress } = useQuery<any>({
    queryKey: ["/api/onboarding/progress"],
    retry: false,
    staleTime: 10_000,
  });

  const { data: lenders = [] } = useQuery<any[]>({
    queryKey: ["/api/lenders"],
    staleTime: 60_000,
  });

  const hasInitializedRef = useRef(false);

  // Resume from DB — runs once on initial data load only; subsequent cache
  // refreshes (triggered by phase saves) must NOT override the user's navigation.
  useEffect(() => {
    if (!progressData) return;
    if (hasInitializedRef.current) return;
    const prog = progressData.progress;
    if (!prog) return;
    hasInitializedRef.current = true;

    if (prog.currentPhase >= 10) {
      // Already completed — redirect to home unless review mode is active
      if (!isReviewMode) {
        setLocation("/");
        return;
      }
    }

    // Resume wizard
    if (prog.currentPhase >= 1) {
      setStep("wizard");
      // In review mode with a completed onboarding, start at phase 1 for review
      const resumePhase = (prog.currentPhase >= 10 && isReviewMode) ? 1 : Math.min(prog.currentPhase, 5);
      setCurrentPhase(resumePhase);
      setConfidenceScore(parseFloat(prog.confidenceScore ?? "0"));
      if (prog.confidenceBreakdown) {
        try {
          setConfidenceBreakdown(JSON.parse(prog.confidenceBreakdown));
        } catch {}
      }

      // Restore phase data from snapshots + profile
      const snapshots = JSON.parse(prog.phaseSnapshots ?? "{}");
      const profile = progressData.profile;
      const vendors = progressData.vendors ?? [];
      const employees = progressData.employees ?? [];

      const restored: PhaseData = {};
      for (const [, v] of Object.entries(snapshots)) {
        Object.assign(restored, v);
      }
      // Fix serialization: leadSourcesRanked may be stored as JSON string in legacy saves
      if (typeof restored.leadSourcesRanked === "string") {
        try { restored.leadSourcesRanked = JSON.parse(restored.leadSourcesRanked as unknown as string); } catch { restored.leadSourcesRanked = []; }
      }
      // Fix serialization: primaryAuctionLanes may be a comma-separated string or JSON array string
      if (typeof restored.primaryAuctionLanes === "string") {
        const raw = (restored.primaryAuctionLanes as unknown as string).trim();
        if (!raw) {
          restored.primaryAuctionLanes = [];
        } else if (raw.startsWith("[")) {
          try { restored.primaryAuctionLanes = JSON.parse(raw); } catch { restored.primaryAuctionLanes = []; }
        } else {
          restored.primaryAuctionLanes = raw.split(",").map((s) => s.trim()).filter(Boolean);
        }
      }
      // Fix serialization: primaryLenders may be stored as JSON string
      if (typeof restored.primaryLenders === "string") {
        try { restored.primaryLenders = JSON.parse(restored.primaryLenders as unknown as string); } catch { restored.primaryLenders = []; }
      }

      // Overlay profile fields
      const safeNum = (raw: any, fallback: any) => {
        const n = parseFloat(raw);
        return Number.isFinite(n) ? n : fallback;
      };
      if (profile) {
        Object.assign(restored, {
          dealershipName: profile.dealershipName ?? restored.dealershipName,
          address: profile.address ?? restored.address,
          city: profile.city ?? restored.city,
          zipCode: profile.zipCode ?? restored.zipCode,
          stateCode: (profile.stateCode ?? restored.stateCode)?.toUpperCase() ?? undefined,
          avgSalesPerMonth: safeNum(profile.avgSalesPerMonth, restored.avgSalesPerMonth),
          avgGrossProfitPerUnit: safeNum(profile.avgGrossProfitPerUnit, restored.avgGrossProfitPerUnit),
          avgVehicleValue: safeNum(profile.avgVehicleValue, restored.avgVehicleValue),
          // Prefer SLA-canonical columns (edited directly on the SLA page) over onboarding-specific columns
          avgFixedCosts: safeNum(profile.monthlyOperatingExpenses, safeNum(profile.avgFixedCosts, restored.avgFixedCosts)),
          targetTurnDays: safeNum(profile.avgTurnDays, safeNum(profile.targetTurnDays, restored.targetTurnDays)),
          avgDaysPurchaseToFrontline: safeNum(profile.avgReconDays, safeNum(profile.avgDaysPurchaseToFrontline, restored.avgDaysPurchaseToFrontline)),
          // avgMonthlyRevenue is now always derived from avgSalesPerMonth × avgVehicleValue — not restored from state
          inventoryType: normalizeInventoryType(profile.inventoryType ?? restored.inventoryType),
          numRooftops: parseFloat(profile.numRooftops) || restored.numRooftops,
          avgUnitsOnLot: parseFloat(profile.avgUnitsOnLot) || restored.avgUnitsOnLot,
          lotCapacity: parseFloat(profile.lotCapacity) || restored.lotCapacity,
          totalHeadcount: parseFloat(profile.totalHeadcount) || restored.totalHeadcount,
        });
      }

      if (vendors.length > 0) restored.vendors = vendors;
      if (employees.length > 0) restored.employees = employees;
      // Normalize legacy inventory type values from snapshots before binding to state
      if (restored.inventoryType) restored.inventoryType = normalizeInventoryType(restored.inventoryType);
      // Derive leadSourcesRanked from marketing vendors when it is missing so the
      // inline form pre-fills with vendor names (mirrors server-side derivation).
      const restoredRanked: string[] = Array.isArray(restored.leadSourcesRanked) ? restored.leadSourcesRanked : [];
      if (restoredRanked.length === 0 && Array.isArray(restored.vendors) && restored.vendors.length > 0) {
        const derivedSources: string[] = (restored.vendors as any[])
          .filter((v: any) => !v.category || v.category === "marketing")
          .map((v: any) => v.name)
          .filter(Boolean);
        if (derivedSources.length > 0) {
          restored.leadSourcesRanked = derivedSources;
        }
      }

      setPhaseData(restored);

      // Recompute missing fields from restored snapshot data so Phase 9 breakdown
      // panel shows correct badges even without a fresh save after page reload.
      setMissingFields(computeMissingFields(restored as Record<string, any>));
    }
  }, [progressData, isReviewMode]);

  const startMutation = useMutation({
    mutationFn: async ({ method, statementId }: { method: "manual" | "statement"; statementId?: string }) => {
      const res = await apiRequest("POST", "/api/onboarding/start", { entryMethod: method, statementId: statementId ?? null });
      return res.json() as Promise<{ progress: any; statementFields: Record<string, any> }>;
    },
    onSuccess: (data) => {
      // Apply any statement-sourced fields if available
      if (data.statementFields && Object.keys(data.statementFields).length > 0) {
        setPhaseData((prev) => ({ ...prev, ...data.statementFields }));
      }
      setStep("wizard");
      setCurrentPhase(1);
      qc.invalidateQueries({ queryKey: ["/api/onboarding/progress"] });
    },
    onError: (err: any) => {
      toast({ title: t("onboarding:toast.failedToStart"), description: err.message, variant: "destructive" });
    },
  });

  const phaseMutation = useMutation({
    mutationFn: async ({ phase, data }: { phase: number; data: any }) => {
      const res = await apiRequest("POST", `/api/onboarding/phase/${phase}`, data);
      return res.json() as Promise<{ progress: any; confidenceScore: number; confidenceBreakdown: ConfidenceBreakdown; missingFields: Array<{ key: string; label: string }> }>;
    },
    onSuccess: (data, variables) => {
      setConfidenceScore(data.confidenceScore ?? confidenceScore);
      if (data.confidenceBreakdown) setConfidenceBreakdown(data.confidenceBreakdown);
      if (data.missingFields) setMissingFields(data.missingFields);
      qc.invalidateQueries({ queryKey: ["/api/onboarding/progress"] });
      // Invalidate dealer profile so the SLA page immediately reflects any saved fields
      // (phases 1, 4, 5 write to profile columns shared with the SLA page)
      if ([1, 4, 5].includes(variables.phase)) {
        qc.invalidateQueries({ queryKey: ["/api/dealer-profile"] });
      }
    },
    onError: (err: any) => {
      toast({ title: t("onboarding:toast.failedToSavePhase"), description: err.message, variant: "destructive" });
    },
  });

  const completeMutation = useMutation({
    mutationFn: async (body: { acceptedFlags: string[]; overriddenFlags: string[] }) => {
      const res = await apiRequest("POST", "/api/onboarding/complete", body);
      return res.json() as Promise<{ confidenceScore: number; profile: any; vendors: any[] }>;
    },
    onSuccess: (data) => {
      setConfidenceScore(data.confidenceScore);
      setCompletedReport({ profile: data.profile, vendors: data.vendors });
      setStep("complete");
      qc.invalidateQueries({ queryKey: ["/api/dealer-profile"] });
      qc.invalidateQueries({ queryKey: ["/api/onboarding/progress"] });
      qc.invalidateQueries({ queryKey: ["/api/auth/me"] });
    },
    onError: (err: any) => {
      toast({ title: t("onboarding:toast.failedToComplete"), description: err.message, variant: "destructive" });
    },
  });

  const missingFieldsMutation = useMutation({
    mutationFn: async (fields: Record<string, any>) => {
      const res = await apiRequest("POST", "/api/onboarding/missing-fields", fields);
      return res.json() as Promise<{ confidenceScore: number; confidenceBreakdown: ConfidenceBreakdown; missingFields: Array<{ key: string; label: string }> }>;
    },
    onSuccess: (data, variables) => {
      setPhaseData((prev) => ({ ...prev, ...variables }));
      setConfidenceScore(data.confidenceScore);
      if (data.confidenceBreakdown) setConfidenceBreakdown(data.confidenceBreakdown);
      if (data.missingFields) setMissingFields(data.missingFields);
      qc.invalidateQueries({ queryKey: ["/api/onboarding/progress"] });
      qc.invalidateQueries({ queryKey: ["/api/dealer-profile"] });
      toast({ title: t("onboarding:toast.profileUpdated"), description: t("onboarding:toast.profileUpdatedDesc") });
    },
    onError: (err: any) => {
      const isHtmlResponse = typeof err?.message === "string" && err.message.startsWith("Failed to execute 'json'");
      const description = isHtmlResponse
        ? t("onboarding:toast.saveFailedHtml")
        : err.message;
      toast({ title: t("onboarding:toast.failedToSave"), description, variant: "destructive" });
    },
  });

  // Background statement upload — starts the extraction fetch without blocking navigation.
  // Called by EntryScreen immediately after the user selects a file; the wizard has
  // already advanced to Phase 1 by the time this resolves.
  const handleUploadInBackground = useCallback(async (file: File) => {
    setBgExtracting(true);
    try {
      // Direct-to-storage: presign → PUT bytes to object storage → finalize.
      const { uploadId } = await uploadFileDirect(file, "onboarding");
      const res = await fetch("/api/onboarding/upload/finalize", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ uploadId }),
        credentials: "include",
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      const fields = data.onboardingFields ?? {};
      if (Object.keys(fields).length > 0) {
        setPhaseData((prev) => ({ ...prev, ...fields }));
        toast({ title: t("onboarding:toast.statementAnalyzed"), description: t("onboarding:toast.statementAnalyzedDesc", { count: Object.keys(fields).length }) });
      } else {
        toast({ title: t("onboarding:toast.statementUploaded"), description: t("onboarding:toast.statementUploadedDesc") });
      }
      qc.invalidateQueries({ queryKey: ["/api/parsed-statements"] });
    } catch (err: any) {
      toast({ title: t("onboarding:toast.statementUploadFailed"), description: err.message || t("onboarding:toast.statementUploadFailedDesc"), variant: "destructive" });
    } finally {
      setBgExtracting(false);
    }
  }, [toast, qc]);

  const handleEntryChoice = (method: "manual" | "statement", statementId?: string, uploadedFields?: Record<string, any>) => {
    const invType = "used_retail" as DealerInventoryType;
    const bench = getBenchmarks(invType);
    setPhaseData({
      inventoryType: "used_retail",
      numRooftops: 1,
      avgUnitsOnLot: bench.avgUnitsOnLot,
      lotCapacity: bench.lotCapacity,
      totalHeadcount: bench.totalHeadcount,
      avgSalesPerMonth: bench.avgSalesPerMonth,
      vendors: bench.defaultVendors.map((v) => ({ ...v, id: crypto.randomUUID() })),
      employees: [
        { role: "Sales", count: bench.salesHeadcount },
        { role: "F&I", count: bench.fiHeadcount },
        { role: "Service / Recon", count: bench.serviceReconHeadcount },
        { role: "Admin", count: bench.adminHeadcount },
      ],
      lendingPrimePct: bench.lendingPrimePct,
      lendingSubprimePct: bench.lendingSubprimePct,
      lendingBhphPct: bench.lendingBhphPct,
      lendingCashPct: bench.lendingCashPct,
      sourcingAuctionPct: bench.sourcingAuctionPct,
      sourcingTradeInPct: bench.sourcingTradeInPct,
      sourcingPrivatePartyPct: bench.sourcingPrivatePartyPct,
      sourcingDealerToDealerPct: bench.sourcingDealerToDealerPct,
      cashReservesRange: bench.cashReservesRange,
      leadToSaleConversionPct: bench.leadToSaleConversionPct,
      // Apply any fields extracted from uploaded statement (override bench defaults)
      ...uploadedFields,
    });
    startMutation.mutate({ method, statementId });
  };

  const updatePhaseData = useCallback((updates: Partial<PhaseData>) => {
    setPhaseData((prev) => {
      const next = { ...prev, ...updates };
      // Re-apply benchmarks if inventory type changed
      if (updates.inventoryType && updates.inventoryType !== prev.inventoryType) {
        const bench = getBenchmarks(updates.inventoryType as DealerInventoryType);
        return {
          ...next,
          lendingPrimePct: bench.lendingPrimePct,
          lendingSubprimePct: bench.lendingSubprimePct,
          lendingBhphPct: bench.lendingBhphPct,
          lendingCashPct: bench.lendingCashPct,
        };
      }
      return next;
    });
  }, []);

  const handleNext = async () => {
    // Enforce 100% total on Phase 2 lending mix
    // Use bench defaults when phaseData values are undefined (same as slider display)
    if (currentPhase === 2) {
      const invType = (phaseData.inventoryType || "used_retail") as DealerInventoryType;
      const bench3 = getBenchmarks(invType);
      const lendingPrime = phaseData.lendingPrimePct ?? bench3.lendingPrimePct;
      const lendingSub = phaseData.lendingSubprimePct ?? bench3.lendingSubprimePct;
      const lendingBhph = phaseData.lendingBhphPct ?? bench3.lendingBhphPct;
      const lendingCash = phaseData.lendingCashPct ?? bench3.lendingCashPct;
      const lendingTotal = lendingPrime + lendingSub + lendingBhph + lendingCash;
      if (Math.abs(lendingTotal - 100) >= 1) {
        toast({ title: t("onboarding:toast.lendingMustTotal"), description: t("onboarding:toast.lendingMustTotalDesc", { total: Math.round(lendingTotal) }), variant: "destructive" });
        return;
      }
    }

    // Enforce 100% total on Phase 5 sourcing mix
    if (currentPhase === 5) {
      const invType = (phaseData.inventoryType || "used_retail") as DealerInventoryType;
      const bench6 = getBenchmarks(invType);
      const srcAuction = phaseData.sourcingAuctionPct ?? bench6.sourcingAuctionPct;
      const srcTrade = phaseData.sourcingTradeInPct ?? bench6.sourcingTradeInPct;
      const srcPrivate = phaseData.sourcingPrivatePartyPct ?? bench6.sourcingPrivatePartyPct;
      const srcD2d = phaseData.sourcingDealerToDealerPct ?? bench6.sourcingDealerToDealerPct;
      const sourcingTotal = srcAuction + srcTrade + srcPrivate + srcD2d;
      if (Math.abs(sourcingTotal - 100) >= 1) {
        toast({ title: t("onboarding:toast.sourcingMustTotal"), description: t("onboarding:toast.sourcingMustTotalDesc", { total: Math.round(sourcingTotal) }), variant: "destructive" });
        return;
      }
    }

    // Save current phase
    const phasePayload = buildPhasePayload(currentPhase, phaseData);
    await phaseMutation.mutateAsync({ phase: currentPhase, data: phasePayload });

    // Phase 5 is the final step — finalize onboarding
    if (currentPhase === 5) {
      completeMutation.mutate({ acceptedFlags: [], overriddenFlags: [] });
      return;
    }

    setCurrentPhase(Math.min(currentPhase + 1, 5));
  };

  const handleBack = () => {
    setCurrentPhase((p) => Math.max(p - 1, 1));
  };

  const runAiValidation = async () => {
    setValidating(true);
    setAiFlags([]);
    try {
      const res = await apiRequest("POST", "/api/onboarding/validate", {
        inventoryType: phaseData.inventoryType,
        phaseData: buildAllPhaseData(phaseData),
      });
      const result: { flags: AiFlag[] } = await res.json();
      setAiFlags((result.flags ?? []).map((f: AiFlag) => ({ ...f, action: undefined })));
    } catch (err: any) {
      toast({ title: t("onboarding:toast.aiValidationFailed"), description: err.message, variant: "destructive" });
    } finally {
      setValidating(false);
    }
  };

  const handleFlagAction = (field: string, action: "keep" | "update") => {
    setAiFlags((flags) => flags.map((f) => f.field === field ? { ...f, action } : f));
  };

  if (loadingProgress) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (step === "entry") return <EntryScreen onChoose={handleEntryChoice} onUploadInBackground={handleUploadInBackground} />;

  if (step === "complete" && completedReport) {
    return (
      <CompletionReport
        data={phaseData}
        profile={completedReport.profile}
        vendors={completedReport.vendors}
        confidenceScore={confidenceScore}
        onGoToDashboard={() => setLocation("/")}
      />
    );
  }

  const canProceed = true;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <div className="border-b bg-background/95 backdrop-blur sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Sparkles className="w-5 h-5 text-primary" />
            <span className="font-semibold text-sm">{t("onboarding:wizard.onboardingTitle")}</span>
          </div>
          <div className="flex items-center gap-3">
            {bgExtracting && (
              <div className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 rounded-full px-3 py-1" data-testid="badge-bg-extracting">
                <span className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin inline-block" />
                {t("onboarding:wizard.analyzingStatement")}
              </div>
            )}
            <div className="text-right">
              <p className="text-xs text-muted-foreground">{t("onboarding:wizard.confidenceScore")}</p>
              <p className={`text-sm font-bold tabular-nums ${confidenceScore >= 70 ? "text-emerald-500" : confidenceScore >= 40 ? "text-amber-500" : "text-muted-foreground"}`} data-testid="confidence-score-header">
                {confidenceScore}%
              </p>
            </div>
            <div className="w-24">
              <Progress value={confidenceScore} className="h-2" />
            </div>
          </div>
        </div>

        {/* Phase progress bar */}
        <div className="max-w-4xl mx-auto px-6 pb-3">
          <div className="flex items-center gap-1">
            {PHASES.map((phase) => {
              const completed = phase.num < currentPhase;
              const active = phase.num === currentPhase;
              const Icon = phase.icon;
              const isClickable = phase.num !== currentPhase;
              const isFuture = !active && !completed;
              const isLocked = false;
              const bubbleContent = (
                <div className={`flex flex-col items-center gap-1 min-w-0 ${isLocked ? "opacity-40" : isFuture ? "opacity-70" : ""} ${isClickable ? "cursor-pointer group" : ""}`}>
                  <div className="relative">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center transition-all ${completed ? "bg-emerald-500 text-white" : active ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"} ${isFuture && isClickable ? "border-2 border-dashed border-muted-foreground/40" : ""} ${isClickable ? "group-hover:ring-2 group-hover:ring-offset-1 group-hover:ring-primary/50 group-hover:scale-110" : ""}`}>
                      {completed ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Icon className="w-3.5 h-3.5" />}
                    </div>
                    {isLocked && (
                      <div className="absolute -bottom-1 -right-1 w-3.5 h-3.5 rounded-full bg-background border border-muted-foreground/40 flex items-center justify-center">
                        <Lock className="w-2 h-2 text-muted-foreground" />
                      </div>
                    )}
                  </div>
                  <span className={`text-[10px] font-medium leading-tight text-center truncate w-full ${completed ? "text-emerald-600 dark:text-emerald-400" : active ? "" : "text-muted-foreground"}`}>{t(`onboarding:${phase.labelKey}`)}</span>
                </div>
              );
              return (
                <div key={phase.num} className="flex items-center gap-1 flex-1">
                  {isClickable ? (
                    <button
                      type="button"
                      data-testid={`phase-bubble-${phase.num}`}
                      title={t("onboarding:wizard.jumpTo", { label: t(`onboarding:${phase.labelKey}`) })}
                      className="flex flex-col items-center min-w-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-md"
                      onClick={async () => {
                        if (phase.num === currentPhase) return;
                        try {
                          const phasePayload = buildPhasePayload(currentPhase, phaseData);
                          await phaseMutation.mutateAsync({ phase: currentPhase, data: phasePayload });
                          setCurrentPhase(phase.num);
                        } catch (_err) {
                          // phaseMutation onError already shows a toast; swallow to prevent unhandled rejection
                        }
                      }}
                    >
                      {bubbleContent}
                    </button>
                  ) : (
                    <div data-testid={`phase-bubble-${phase.num}`}>{bubbleContent}</div>
                  )}
                  {phase.num < 5 && <div className={`flex-1 h-0.5 mb-3 transition-colors ${completed ? "bg-emerald-500" : "bg-muted"}`} />}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-4xl mx-auto px-6 py-8">
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-1">
              <Badge variant="secondary" className="text-xs">{t("onboarding:wizard.phaseOf", { current: currentPhase })}</Badge>
            </div>
            <h2 className="text-2xl font-bold">
              {t(`onboarding:wizard.titles.${currentPhase}`)}
            </h2>
          </div>

          <div className="min-h-[400px]">
            {currentPhase === 1 && <Phase1 data={phaseData} onChange={updatePhaseData} />}
            {currentPhase === 2 && <Phase3 data={phaseData} onChange={updatePhaseData} />}
            {currentPhase === 3 && <Phase2 data={phaseData} onChange={updatePhaseData} />}
            {currentPhase === 4 && <Phase4 data={phaseData} onChange={updatePhaseData} />}
            {currentPhase === 5 && <Phase6 data={phaseData} onChange={updatePhaseData} />}
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t">
            <Button
              variant="outline"
              onClick={handleBack}
              disabled={currentPhase === 1}
              data-testid="button-phase-back"
              className="gap-2"
            >
              <ChevronLeft className="w-4 h-4" /> {t("onboarding:wizard.back")}
            </Button>

            <div className="flex items-center gap-3">
              <Button
                onClick={handleNext}
                disabled={phaseMutation.isPending || completeMutation.isPending || !canProceed}
                data-testid="button-phase-next"
                className="gap-2"
              >
                {phaseMutation.isPending || completeMutation.isPending ? (
                  <><RefreshCw className="w-4 h-4 animate-spin" /> {t("onboarding:wizard.saving")}</>
                ) : currentPhase === 5 ? (
                  <><CheckCheck className="w-4 h-4" /> {t("onboarding:wizard.finalizeOnboarding")}</>
                ) : (
                  <>{t("onboarding:wizard.next")} <ChevronRight className="w-4 h-4" /></>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Helper: build phase payload ──────────────────────────────────────────────

function buildPhasePayload(phase: number, data: PhaseData): Record<string, any> {
  switch (phase) {
    case 1:
      return {
        dealershipName: data.dealershipName,
        address: data.address,
        city: data.city,
        zipCode: data.zipCode,
        stateCode: data.stateCode,
        avgSalesPerMonth: typeof data.avgSalesPerMonth === "number" ? data.avgSalesPerMonth : undefined,
        avgGrossProfitPerUnit: typeof data.avgGrossProfitPerUnit === "number" ? data.avgGrossProfitPerUnit : undefined,
        avgVehicleValue: typeof data.avgVehicleValue === "number" ? data.avgVehicleValue : undefined,
        avgFixedCosts: typeof data.avgFixedCosts === "number" ? data.avgFixedCosts : undefined,
        targetTurnDays: typeof data.targetTurnDays === "number" ? data.targetTurnDays : undefined,
        avgDaysPurchaseToFrontline: typeof data.avgDaysPurchaseToFrontline === "number" ? data.avgDaysPurchaseToFrontline : undefined,
        avgMonthlyRevenue: (typeof data.avgSalesPerMonth === "number" && typeof data.avgVehicleValue === "number")
          ? data.avgSalesPerMonth * data.avgVehicleValue
          : undefined,
        inventoryType: data.inventoryType,
        numRooftops: typeof data.numRooftops === "number" ? data.numRooftops : undefined,
        avgUnitsOnLot: typeof data.avgUnitsOnLot === "number" ? data.avgUnitsOnLot : undefined,
        lotCapacity: typeof data.lotCapacity === "number" ? data.lotCapacity : undefined,
        totalHeadcount: typeof data.totalHeadcount === "number" ? data.totalHeadcount : undefined,
      };
    case 2: {
      const bench2 = getBenchmarks((data.inventoryType || "used_retail") as DealerInventoryType);
      return {
        avgSalesPerMonth: typeof data.avgSalesPerMonth === "number" ? data.avgSalesPerMonth : undefined,
        lendingPrimePct: data.lendingPrimePct ?? bench2.lendingPrimePct,
        lendingSubprimePct: data.lendingSubprimePct ?? bench2.lendingSubprimePct,
        lendingBhphPct: data.lendingBhphPct ?? bench2.lendingBhphPct,
        lendingCashPct: data.lendingCashPct ?? bench2.lendingCashPct,
      };
    }
    case 3: {
      const vendors3 = data.vendors ?? [];
      const mktVendors3 = vendors3.filter((v) => !v.category || v.category === "marketing");
      const totalLeads3 = mktVendors3.reduce((s, v) => s + (typeof v.leadsPerMonth === "number" ? v.leadsPerMonth : 0), 0);
      const totalSales3 = mktVendors3.reduce((s, v) => s + (typeof v.salesPerMonth === "number" ? v.salesPerMonth : 0), 0);
      // When leads exist, always compute conversion (0% is valid and meaningful)
      const computedConvPct = totalLeads3 > 0 ? parseFloat(((totalSales3 / totalLeads3) * 100).toFixed(2)) : undefined;
      const computedLeadVolume = totalLeads3 > 0 ? totalLeads3 : undefined;
      return {
        dmsSystem: data.dmsSystem,
        dmsMonthlyCost: typeof data.dmsMonthlyCost === "number" ? data.dmsMonthlyCost : undefined,
        vendors: vendors3.map((v) => ({
          id: v.id,
          name: v.name,
          category: v.category || "marketing",
          monthlyCost: typeof v.monthlyCost === "number" ? v.monthlyCost : null,
          leadsPerMonth: typeof v.leadsPerMonth === "number" ? v.leadsPerMonth : null,
          salesPerMonth: typeof v.salesPerMonth === "number" ? v.salesPerMonth : null,
        })),
        employees: data.employees ?? [],
        leadSourcesRanked: mktVendors3.map((v) => v.name).filter(Boolean),
        tracksRepeatReferral: data.tracksRepeatReferral,
        repeatReferralPct: data.repeatReferralPct,
        // Always persist computed aggregates (including zero) to clear stale profile values
        monthlyLeadVolume: computedLeadVolume ?? 0,
        leadToSaleConversionPct: computedConvPct ?? null,
      };
    }
    case 4:
      return {
        avgFrontEndGross: typeof data.avgFrontEndGross === "number" ? data.avgFrontEndGross : undefined,
        avgBackEndGross: typeof data.avgBackEndGross === "number" ? data.avgBackEndGross : undefined,
        avgFixedCosts: typeof data.avgFixedCosts === "number" ? data.avgFixedCosts : undefined,
        avgVariableCostPerUnit: typeof data.avgVariableCostPerUnit === "number" ? data.avgVariableCostPerUnit : undefined,
        avgReconCost: typeof data.avgReconCost === "number" ? data.avgReconCost : undefined,
        avgTransportFee: typeof data.avgTransportFee === "number" ? data.avgTransportFee : undefined,
      };
    case 5: {
      const bench5 = getBenchmarks((data.inventoryType || "used_retail") as DealerInventoryType);
      return {
        sourcingAuctionPct: data.sourcingAuctionPct ?? bench5.sourcingAuctionPct,
        sourcingTradeInPct: data.sourcingTradeInPct ?? bench5.sourcingTradeInPct,
        sourcingPrivatePartyPct: data.sourcingPrivatePartyPct ?? bench5.sourcingPrivatePartyPct,
        sourcingDealerToDealerPct: data.sourcingDealerToDealerPct ?? bench5.sourcingDealerToDealerPct,
        primaryAuctionLanes: data.primaryAuctionLanes,
        avgDaysPurchaseToFrontline: typeof data.avgDaysPurchaseToFrontline === "number" ? data.avgDaysPurchaseToFrontline : undefined,
      };
    }
    case 6:
      return {
        cashReservesRange: data.cashReservesRange,
        hasLineOfCredit: data.hasLineOfCredit,
        lineOfCreditRange: data.lineOfCreditRange,
        hasNonFloorplanDebt: data.hasNonFloorplanDebt,
        nonFloorplanDebtMonthly: typeof data.nonFloorplanDebtMonthly === "number" ? data.nonFloorplanDebtMonthly : undefined,
        bhphReceivablesRange: data.bhphReceivablesRange,
      };
    default:
      return {};
  }
}

function buildAllPhaseData(data: PhaseData): Record<string, any> {
  const result: Record<string, any> = {};
  for (let p = 1; p <= 5; p++) {
    Object.assign(result, buildPhasePayload(p, data));
  }
  return result;
}
